// components/LandingPage.js - Complete with Fixed Mobile Menu + Trial Rewards
// 2026-11: #features rebuilt as a four-workspace showcase (Live Watch, Short Watch, Replay, ScalpWatch)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowRight, 
  CheckCircle, 
  Shield, 
  Zap, 
  BarChart3,
  Target,
  Moon,
  Sun,
  ChevronRight,
  Star,
  Users,
  Activity,
  Lock,
  PlayCircle,
  Menu,
  X,
  Globe,
  Brain,
  Rocket, 
  User,
  ExternalLink,
  Mail,
  Clock,
  Search,
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertTriangle,
  DollarSign,
  Info,
  ChevronDown,
  Crosshair,    
  Eye,
  Volume2,
  Download,
  RefreshCw,
  Bell,
  ChevronLeft,
  VolumeX,
  RotateCcw,
  Chrome,
  Gift,
  Percent,
  Trophy,
} from 'lucide-react';
import tunaLogo from '../assets/tunasignalai_logo.png';

// Chrome Web Store listing for the TunaSignal → Webull Watchlist Sync extension
const TRUSTPILOT_URL = 'https://www.trustpilot.com/review/tunasignal.ai';
const WEBULL_EXTENSION_URL = 'https://chromewebstore.google.com/detail/dhgdpcbphhlnecgklanbhbcebidhddbg';

// ============================================================================
// TRIAL OFFER
// Single source of truth for every trial/offer claim on this page (hero pill,
// offer section, pricing cards, FAQ + JSON-LD, meta tags, onboarding tour).
// These terms MUST match backend enforcement in app.py — change both together.
//   - Trial = the user's first N US market days (Mon–Fri, excl. NYSE holidays,
//     ET boundary). Sign-up on a weekend/holiday starts on the next market day.
//   - No activity requirement. No card. Nothing is charged when the trial ends.
//   - Discount: subscribing during the trial or within redeemWindowHours after it applies
//     discountPct off the first discountMonths, then standardPrice.
//   - Users who started under the old 15+15-day program keep their old terms.
// ============================================================================
const TRIAL = {
  marketDays: 5,          // free trial length in US market days, no card
  discountPct: 50,        // off the first discountMonths when subscribing in the window
  discountMonths: 6,      // length of the discount once subscribed
  redeemWindowHours: 72,  // hours after trial end to subscribe at the discount
  standardPrice: 200,     // USD / month
};
const DISCOUNTED_PRICE = Math.round(TRIAL.standardPrice * (100 - TRIAL.discountPct) / 100);
const TRIAL_SHORT = `${TRIAL.marketDays} market days free · no credit card`;
const TRIAL_OFFER_SENTENCE =
  `Subscribe during your trial or within ${TRIAL.redeemWindowHours} hours after and get ${TRIAL.discountPct}% off your first ` +
  `${TRIAL.discountMonths} months: $${DISCOUNTED_PRICE}/month, then $${TRIAL.standardPrice}/month.`;
const MARKET_DAY_RULE =
  'A market day is any US trading day (Monday–Friday, excluding NYSE holidays, Eastern Time). ' +
  'Weekends and market holidays don\'t use up your trial.';

// ============================================================================
// LANDING ONBOARDING OVERLAY
// A short, skippable "how it works" tour shown on the marketing page — the same
// slide/overlay experience as the in-app tours, but concept-only (no proprietary
// detail) and with NO audio narration, since visitors haven't signed up yet.
// It ends on a sign-up CTA. Trigger it from the hero / nav "How it works" button;
// dismissal is remembered in localStorage so returning visitors aren't nagged.
// ============================================================================
const LANDING_TOUR_STORAGE_KEY = 'tunasignal_landing_tour_seen';
const LANDING_TOUR_VOICE_KEY = 'tunasignal_landing_tour_voice';
const LANDING_AUDIO_BASE = '/audio/onboarding/';

const LandingOnboardingOverlay = ({ darkMode, onClose, onGetStarted, audioBase = LANDING_AUDIO_BASE }) => {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-advance: after a slide's narration finishes, wait, then advance. Any
  // manual nav, voice-off, replay, or the last slide cancels it. Falls back to a
  // fixed timer when voice is off (so a silent tour still progresses).
  const AUTO_ADVANCE_MS = 5000;
  const SILENT_ADVANCE_MS = 7000;
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [advancePending, setAdvancePending] = useState(false);
  const advanceTimerRef = useRef(null);
  const cancelAutoAdvance = useCallback(() => {
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    setAdvancePending(false);
  }, []);

  // Voice narration on/off (remembered across sessions; intro gate still shown).
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem(LANDING_TOUR_VOICE_KEY) !== 'off'; } catch (e) { return true; }
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioAvailable, setAudioAvailable] = useState(true);
  const audioRef = useRef(null);
  const playTokenRef = useRef(0);

  const persistVoice = (on) => {
    try { localStorage.setItem(LANDING_TOUR_VOICE_KEY, on ? 'on' : 'off'); } catch (e) {}
  };

  const card = darkMode ? 'bg-[#0D1E2E] border-[#1C3348]' : 'bg-white border-slate-200';
  const textMain = darkMode ? 'text-white' : 'text-slate-900';
  const textSub = darkMode ? 'text-slate-300' : 'text-slate-600';
  const chipBg = darkMode ? 'bg-[#13283C]/60' : 'bg-slate-50';
  const chipBorder = darkMode ? 'border-[#28445F]' : 'border-slate-200';

  // Reusable bits -------------------------------------------------------------
  const StepBadge = ({ icon: Icon, color }) => {
    const map = {
      blue:   darkMode ? 'bg-blue-900/30 text-blue-400'     : 'bg-blue-100 text-blue-600',
      purple: darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600',
      green:  darkMode ? 'bg-green-900/30 text-green-400'   : 'bg-green-100 text-green-600',
      orange: darkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-600',
      cyan:   darkMode ? 'bg-cyan-900/30 text-cyan-400'     : 'bg-cyan-100 text-cyan-600',
    };
    return (
      <div className={`inline-flex p-3 rounded-xl ${map[color] || map.blue}`}>
        <Icon className="w-6 h-6" />
      </div>
    );
  };

  // Small numbered step for the workflow slide.
  const FlowStep = ({ n, children }) => (
    <div className="flex items-center space-x-2.5">
      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>{n}</span>
      <p className={`text-xs leading-relaxed ${textSub}`}>{children}</p>
    </div>
  );

  // Score/price ladder row.
  const LadderRow = ({ range, label, color }) => {
    const bar = { purple: 'bg-purple-500', blue: 'bg-blue-500', green: 'bg-green-500', yellow: 'bg-yellow-500', red: 'bg-red-500', gray: 'bg-slate-400' };
    const tx = {
      purple: darkMode ? 'text-purple-300' : 'text-purple-700',
      blue:   darkMode ? 'text-blue-300'   : 'text-blue-700',
      green:  darkMode ? 'text-green-300'  : 'text-green-700',
      yellow: darkMode ? 'text-yellow-300' : 'text-yellow-700',
      red:    darkMode ? 'text-red-300'    : 'text-red-700',
      gray:   darkMode ? 'text-slate-400'   : 'text-slate-500',
    };
    return (
      <div className="flex items-center gap-2.5">
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${bar[color]}`} />
        <span className={`text-xs font-bold min-w-[86px] ${tx[color]}`}>{range}</span>
        <span className={`text-[11px] ${textSub}`}>{label}</span>
      </div>
    );
  };

  const TagChip = ({ label, cls }) => (
    <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide border ${cls}`}>
      {label}
    </span>
  );

  // Slides — concept only, no proprietary internals -------------------------
  const STEPS = [
    {
      key: 'landing_why',
      icon: TrendingUp, color: 'blue',
      title: 'Find movers before everyone else',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            Most traders find a stock <em>after</em> it's already made its big move. TunaSignal scans
            thousands of stocks throughout the session and surfaces unusual setups <strong className={textMain}>early</strong> —
            before they attract widespread attention.
          </p>
          <div className={`p-3 rounded-md border ${chipBorder} ${chipBg} space-y-2`}>
            <div className="flex items-center gap-2 text-[11px]">
              <span className={`font-bold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Most traders</span>
              <span className={textSub}>stock → <span className="text-green-500 font-semibold">+80%</span> → notice it</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className={`font-bold ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>TunaSignal</span>
              <span className={textSub}>stock → <span className="text-blue-500 font-semibold">signal</span> → research → decide</span>
            </div>
          </div>
          <p className={`text-[11px] leading-relaxed ${textSub}`}>
            The goal isn't to predict the future — it's to help you find opportunities earlier and
            research them faster.
          </p>
        </div>
      ),
    },
    {
      key: 'landing_score',
      icon: Target, color: 'purple',
      title: 'The TunaSignalAI Score',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            Every stock gets a <strong className={textMain}>score from 0–100</strong> — a single read on
            how much is lining up in its favor. Sort the whole market by it in one click.
          </p>
          <div className={`p-3 rounded-md border ${chipBorder} ${chipBg} space-y-2`}>
            <LadderRow range="95–100%" label="Highest conviction" color="purple" />
            <LadderRow range="85–94%" label="Exceptional — Prime Watch Zone" color="blue" />
            <LadderRow range="65–84%" label="Building momentum" color="green" />
            <LadderRow range="0–64%" label="Lower priority" color="gray" />
          </div>
          <p className={`text-[11px] leading-relaxed ${textSub}`}>
            Higher means stronger analytical confluence — <strong className={textMain}>85%+</strong> is
            where the strongest setups live.
          </p>
        </div>
      ),
    },
    {
      key: 'landing_early',
      icon: Zap, color: 'green',
      title: 'Are you early or late?',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            A <strong className={textMain}>Live Price %</strong> shows how far a name has moved
            <em> since</em> it was flagged — so you can tell if you're ahead of the crowd or chasing.
          </p>
          <div className={`p-3 rounded-md border ${chipBorder} ${chipBg} space-y-2`}>
            <LadderRow range="−5% to +5%" label="Early" color="green" />
            <LadderRow range="+5% to +15%" label="Early — gaining traction" color="blue" />
            <LadderRow range="+15% to +25%" label="Developing — verify volume" color="yellow" />
            <LadderRow range="+25%+" label="Late — move likely happened" color="red" />
          </div>
          <p className={`text-[11px] leading-relaxed ${textSub}`}>
            A 90% score is great. A 90% score that's only moved <strong className={textMain}>+2%</strong> since
            detection may be even more interesting — that's the edge.
          </p>
        </div>
      ),
    },
    {
      key: 'landing_tags',
      icon: Eye, color: 'orange',
      title: 'Read the tags',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            Color-coded badges flag opportunity and risk at a glance — so you know what you're looking
            at before you dig in.
          </p>
          <div>
            <p className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>Opportunity</p>
            <div className="space-y-1.5">
              <div className={`flex items-center gap-2.5 p-2 rounded-md border ${chipBorder} ${chipBg}`}>
                <TagChip label="SwingTrade" cls="bg-emerald-500/20 text-emerald-300 border-emerald-500/40" />
                <span className={`text-[11px] ${textSub}`}>Setup that can hold overnight</span>
              </div>
              <div className={`flex items-center gap-2.5 p-2 rounded-md border ${chipBorder} ${chipBg}`}>
                <TagChip label="+100%" cls="bg-lime-500/20 text-lime-300 border-lime-500/40" />
                <span className={`text-[11px] ${textSub}`}>Live runner — gain since it was ranked</span>
              </div>
            </div>
          </div>
          <div>
            <p className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 ${darkMode ? 'text-rose-300' : 'text-rose-600'}`}>Risk</p>
            <div className="space-y-1.5">
              <div className={`flex items-center gap-2.5 p-2 rounded-md border ${chipBorder} ${chipBg}`}>
                <TagChip label="Illiquid" cls="bg-slate-500/20 text-slate-300 border-slate-500/40" />
                <span className={`text-[11px] ${textSub}`}>Too thin to trade safely</span>
              </div>
              <div className={`flex items-center gap-2.5 p-2 rounded-md border ${chipBorder} ${chipBg}`}>
                <TagChip label="Dilution Risk" cls="bg-rose-500/20 text-rose-300 border-rose-500/40" />
                <span className={`text-[11px] ${textSub}`}>Share-offering pressure on file</span>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'landing_workflow',
      icon: Search, color: 'cyan',
      title: 'How most people use it',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            It comes together as a simple daily routine — no guesswork:
          </p>
          <div className={`p-3 rounded-md border ${chipBorder} ${chipBg} space-y-2.5`}>
            <FlowStep n="1">Open the dashboard and <strong className={textMain}>sort by Score</strong></FlowStep>
            <FlowStep n="2">Check the <strong className={textMain}>Live Price %</strong> — still early?</FlowStep>
            <FlowStep n="3">Scan the <strong className={textMain}>tags</strong> for risk &amp; opportunity</FlowStep>
            <FlowStep n="4">Open the <strong className={textMain}>chart</strong> to see the setup</FlowStep>
            <FlowStep n="5">Set an <strong className={textMain}>alert</strong> or add to your watchlist</FlowStep>
          </div>
          <p className={`text-[11px] leading-relaxed ${textSub}`}>
            Score → Live Price % → Tags → Chart → Alert. Find fast, research well, let alerts do the watching.
          </p>
        </div>
      ),
    },
    {
      key: 'landing_alerts',
      icon: Bell, color: 'orange',
      title: 'Stop watching charts all day',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            <strong className={textMain}>Subscribe once</strong> and let high-conviction alerts come to
            you — on desktop or <strong className={textMain}>Telegram</strong>, wherever you are.
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {['Score changes', 'MACD crossovers', 'Volume spikes', 'SEC filings', 'News alerts', 'Telegram push'].map((a, i) => (
              <div key={i} className={`flex items-center gap-1.5 p-1.5 rounded-md border ${chipBorder} ${chipBg}`}>
                <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                <span className={`text-[11px] ${textSub}`}>{a}</span>
              </div>
            ))}
          </div>
          <div className={`p-2.5 rounded-md border-l-4 border-green-500 ${darkMode ? 'bg-green-900/15' : 'bg-green-50'}`}>
            <p className={`text-[11px] ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Tune it to the strongest setups so you <strong>never miss an 85%+ signal</strong> — without
              drowning in noise.
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'landing_start',
      icon: Rocket, color: 'blue',
      title: 'Ready to try it?',
      body: (
        <div className="space-y-3">
          <p className={`text-sm leading-relaxed ${textSub}`}>
            Start free for {TRIAL.marketDays} market days — <strong className={textMain}>no credit card required</strong>.
          </p>
          <div className={`p-3 rounded-md border ${chipBorder} ${chipBg} space-y-2`}>
            <div className="flex items-center gap-2.5">
              <Gift className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className={`text-[11px] ${textSub}`}>
                {TRIAL.marketDays} US market days → <strong className={textMain}>full platform access</strong>
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <Percent className="w-4 h-4 text-purple-500 flex-shrink-0" />
              <span className={`text-[11px] ${textSub}`}>
                Subscribe during or {TRIAL.redeemWindowHours}h after → <strong className={textMain}>{TRIAL.discountPct}% off for {TRIAL.discountMonths} months</strong>
              </span>
            </div>
          </div>
          <div className={`p-3 rounded-md border ${chipBorder} ${chipBg}`}>
            <p className={`text-[11px] font-bold uppercase tracking-wide mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>What's included</p>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
              {['Live dashboard', 'TunaSignalAI Scores', 'Watchlists', 'Live alerts', 'Telegram integration', 'No card to start'].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span className={`text-[11px] ${textSub}`}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`p-3 rounded-md border-l-4 border-red-500 ${darkMode ? 'bg-red-900/15' : 'bg-red-50'}`}>
            <p className={`text-[11px] leading-relaxed ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              TunaSignal is a research &amp; analytics platform — <strong>not investment advice</strong>.
              Scores and signals are informational only; trading involves substantial risk.
            </p>
          </div>
        </div>
      ),
    },
  ];

  const total = STEPS.length;
  const current = STEPS[step];
  const isLast = step === total - 1;

  // Audio helpers ------------------------------------------------------------
  const playCurrent = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    const token = ++playTokenRef.current;
    const src = `${audioBase}${STEPS[step].key}.mp3`;
    try {
      const abs = new URL(src, window.location.href).href;
      if (a.src !== abs) { a.src = src; a.load(); }
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { if (playTokenRef.current === token) setIsPlaying(true); })
         .catch(() => { if (playTokenRef.current === token) setIsPlaying(false); });
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBase, step]);

  const stopAudio = useCallback(() => {
    playTokenRef.current++;            // invalidate any pending play() promise
    const a = audioRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch (e) {} }
    setIsPlaying(false);
  }, []);

  const goNext = useCallback(() => { cancelAutoAdvance(); setStep((s) => Math.min(s + 1, STEPS.length - 1)); }, [cancelAutoAdvance, STEPS.length]);
  const goPrev = useCallback(() => { cancelAutoAdvance(); setStep((s) => Math.max(s - 1, 0)); }, [cancelAutoAdvance]);
  const goToStep = useCallback((i) => { cancelAutoAdvance(); setStep(i); }, [cancelAutoAdvance]);

  const finish = useCallback(() => {
    cancelAutoAdvance();
    stopAudio();
    try { localStorage.setItem(LANDING_TOUR_STORAGE_KEY, '1'); } catch (e) {}
    onClose();
  }, [cancelAutoAdvance, stopAudio, onClose]);

  const takeAction = () => {
    cancelAutoAdvance();
    stopAudio();
    try { localStorage.setItem(LANDING_TOUR_STORAGE_KEY, '1'); } catch (e) {}
    onGetStarted();
  };

  const beginTour = (withVoice) => {
    setVoiceOn(withVoice);
    persistVoice(withVoice);
    setStarted(true);
    if (withVoice) {
      // play in the same tick as the click gesture (browsers require it)
      setTimeout(() => playCurrent(), 0);
    }
  };

  const toggleVoice = () => {
    setVoiceOn((on) => {
      const next = !on;
      persistVoice(next);
      if (next) { setTimeout(() => playCurrent(), 0); }
      else { stopAudio(); cancelAutoAdvance(); }
      return next;
    });
  };

  const replayNarration = () => {
    cancelAutoAdvance();
    if (!voiceOn) { toggleVoice(); return; }
    playCurrent();
  };

  // Schedule an auto-advance to the next slide after `ms`.
  const scheduleAdvance = useCallback((ms) => {
    if (!autoAdvance) return;
    setStep((s) => {
      if (s >= STEPS.length - 1) return s;   // stop on the last slide
      setAdvancePending(true);
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null;
        setAdvancePending(false);
        setStep((cur) => Math.min(cur + 1, STEPS.length - 1));
      }, ms);
      return s;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvance, STEPS.length]);

  // Narration finished → advance after a short beat.
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    scheduleAdvance(AUTO_ADVANCE_MS);
  }, [scheduleAdvance]);

  // Play narration when the step changes (Next/Back/dot) with voice on. The
  // first playback is kicked off by the intro gate / voice toggle (a user
  // gesture), so we skip the very first run here to avoid a double play().
  const prevStepRef = useRef(step);
  const startedOnceRef = useRef(false);
  useEffect(() => {
    const stepChanged = prevStepRef.current !== step;
    prevStepRef.current = step;
    if (!started || !voiceOn) return;
    if (!startedOnceRef.current) { startedOnceRef.current = true; return; }
    if (stepChanged) playCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, started, voiceOn]);

  // Silent fallback: when the tour runs with voice OFF (or audio unavailable),
  // advance on a fixed timer so a muted tour still progresses.
  useEffect(() => {
    if (!started) return;
    if (voiceOn && audioAvailable) return;   // audio path handles advancing
    if (!autoAdvance || step >= STEPS.length - 1) return;
    setAdvancePending(true);
    const t = setTimeout(() => { setAdvancePending(false); setStep((s) => Math.min(s + 1, STEPS.length - 1)); }, SILENT_ADVANCE_MS);
    return () => { clearTimeout(t); setAdvancePending(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, voiceOn, audioAvailable, autoAdvance, step]);

  // Stop narration on unmount.
  useEffect(() => {
    return () => {
      playTokenRef.current++;
      if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
      const a = audioRef.current;
      if (a) { try { a.pause(); a.currentTime = 0; } catch (e) {} }
    };
  }, []);

  // Lock scroll + keyboard nav.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (!started) return;
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [started, finish, goNext, goPrev]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
      onClick={finish}
      role="dialog"
      aria-modal="true"
      aria-label="How TunaSignal works"
    >
      <div
        className={`w-full ${!started ? 'sm:max-w-lg' : 'sm:max-w-md'} rounded-t-2xl sm:rounded-xl border shadow-2xl flex flex-col overflow-hidden max-h-[92vh] sm:max-h-[90vh] ${card}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Narration audio — always mounted so the ref is stable. */}
        <audio
          ref={audioRef}
          preload="none"
          onEnded={handleAudioEnded}
          onError={() => { setAudioAvailable(false); setIsPlaying(false); }}
          onCanPlay={() => setAudioAvailable(true)}
        />

        {!started ? (
          /* Intro gate */
          <div className="px-6 py-8 sm:px-10 sm:py-12 text-center overflow-y-auto">
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className={`w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-3xl flex items-center justify-center ${darkMode ? 'bg-blue-500/15' : 'bg-blue-100'}`}>
                <Rocket className="w-7 h-7 sm:w-10 sm:h-10 text-blue-500" />
              </div>
            </div>
            <h3 className={`text-xl sm:text-3xl font-bold ${textMain}`}>How TunaSignal works</h3>
            <p className={`text-sm sm:text-base mt-2 sm:mt-3 mb-1 max-w-md mx-auto ${textSub}`}>
              A quick tour — how to find movers early, read the tags, and let
              alerts do the watching. Under two minutes.
            </p>
            <p className={`text-sm sm:text-base font-medium mt-5 sm:mt-7 mb-3 sm:mb-4 ${textMain}`}>
              Would you like voice narration?
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
              <button
                onClick={() => beginTour(true)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md sm:rounded-xl text-sm sm:text-base font-semibold bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5] transition-colors sm:flex-1"
              >
                <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /> Start with narration
              </button>
              <button
                onClick={() => beginTour(false)}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md sm:rounded-xl text-sm sm:text-base font-semibold border transition-colors sm:flex-1 ${
                  darkMode ? 'border-[#28445F] text-slate-200 hover:bg-[#13283C]' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" /> Start without sound
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 mt-5 sm:mt-7">
              <button
                onClick={takeAction}
                className="text-xs sm:text-sm font-semibold text-blue-500 hover:text-blue-600"
              >
                Skip to free trial
              </button>
              <span className={darkMode ? 'text-slate-600' : 'text-slate-300'}>·</span>
              <button
                onClick={finish}
                className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Maybe later
              </button>
            </div>
          </div>
        ) : (
          <React.Fragment>
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b ${darkMode ? 'border-[#1C3348]' : 'border-slate-200'}`}>
              <div className="flex items-center space-x-2.5 min-w-0">
                <StepBadge icon={current.icon} color={current.color} />
                <div className="min-w-0">
                  <p className={`text-[11px] uppercase tracking-wide font-semibold ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    How it works · {step + 1} of {total}
                  </p>
                  <h3 className={`text-base font-bold truncate ${textMain}`}>{current.title}</h3>
                </div>
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0">
                {voiceOn && audioAvailable && (
                  <button
                    onClick={replayNarration}
                    className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-[#13283C] text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                    aria-label="Replay narration"
                    title="Replay narration"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={toggleVoice}
                  className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    voiceOn
                      ? (darkMode ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                      : (darkMode ? 'hover:bg-[#13283C] text-slate-400' : 'hover:bg-slate-100 text-slate-500')
                  }`}
                  aria-pressed={voiceOn}
                  title={voiceOn ? 'Voice narration on' : 'Voice narration off'}
                >
                  {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  <span className="hidden sm:inline">{voiceOn ? 'Voice on' : 'Voice off'}</span>
                </button>
                <button
                  onClick={finish}
                  className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-[#13283C] text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 overflow-y-auto">
              {current.body}
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center space-x-1.5 pb-3">
              {STEPS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goToStep(i)}
                  aria-label={`Go to step ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === step ? 'w-6 bg-blue-500' : `w-1.5 ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-300 hover:bg-slate-400'}`
                  }`}
                />
              ))}
            </div>

            {/* Narration status / auto-advance */}
            {voiceOn && audioAvailable && (
              <div className={`flex items-center justify-center gap-2 pb-3 text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {advancePending ? (
                  <>
                    <Clock className="w-3 h-3 text-blue-500" />
                    <span>Next slide shortly…</span>
                    <button
                      onClick={cancelAutoAdvance}
                      className={`font-semibold underline-offset-2 hover:underline ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}
                    >
                      Stay
                    </button>
                  </>
                ) : (
                  <>
                    <Volume2 className={`w-3 h-3 ${isPlaying ? 'text-blue-500' : ''}`} />
                    <span>{isPlaying ? 'Narrating…' : 'Narration ready'}</span>
                    <span className={darkMode ? 'text-slate-600' : 'text-slate-300'}>·</span>
                    <button
                      onClick={() => { setAutoAdvance((v) => { if (v) cancelAutoAdvance(); return !v; }); }}
                      className={`hover:underline underline-offset-2 ${autoAdvance ? (darkMode ? 'text-blue-300' : 'text-blue-600') : ''}`}
                      title="Automatically move to the next slide after each narration"
                    >
                      Auto-advance {autoAdvance ? 'on' : 'off'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Footer */}
            <div className={`flex items-center justify-between px-5 py-4 border-t ${darkMode ? 'border-[#1C3348]' : 'border-slate-200'}`}>
              <button
                onClick={finish}
                className={`text-xs font-medium ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Skip
              </button>
              <div className="flex items-center space-x-2">
                {step > 0 && (
                  <button
                    onClick={goPrev}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      darkMode ? 'border-[#28445F] text-slate-200 hover:bg-[#13283C]' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </button>
                )}
                {!isLast ? (
                  <button
                    onClick={goNext}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5] transition-colors"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                ) : (
                  <button
                    onClick={takeAction}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
                  >
                    <Rocket className="w-4 h-4 mr-1.5" /> Start free trial
                  </button>
                )}
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

// Concentric "bullseye" rings taken from the logo's target. Purely decorative.
const BullseyeRings = ({ className = '', color = 'currentColor', rings = 5 }) => (
  <svg viewBox="0 0 400 400" className={className} aria-hidden="true" fill="none">
    {Array.from({ length: rings }).map((_, i) => (
      <circle key={i} cx="200" cy="200" r={190 - i * (170 / rings)} stroke={color} strokeWidth={i % 2 ? 1 : 2} />
    ))}
    <circle cx="200" cy="200" r="10" fill={color} />
  </svg>
);

// Verified results for the hero (newest weekly report) — published weekly reports, not
// live signals. Reads /api/public/reports/weekly-summary (app.py), which parses
// the newest weekly report. The snapshot below is only a fallback for when the
// API is unreachable; the live endpoint always wins.
const WEEKLY_FALLBACK = {"ok":true,"latest":{"all_time_signals":1729,"buckets":[{"band":"85-89","hit_rate":11,"hit_rate_all_time":11,"n_all_time":481,"signals":35},{"band":"90-94","hit_rate":25,"hit_rate_all_time":17,"n_all_time":746,"signals":77},{"band":"95+","hit_rate":15,"hit_rate_all_time":20,"n_all_time":502,"signals":52}],"date":"2026-09-25","hit_rate":19,"missed":102,"movers":[{"hours_to_peak":13.0,"peak_pct":252,"score":90,"ticker":"ARTL"},{"hours_to_peak":15.9,"peak_pct":143,"score":91,"ticker":"GCTK"},{"hours_to_peak":4.7,"peak_pct":128,"score":95,"ticker":"VEEE"},{"hours_to_peak":24.3,"peak_pct":122,"score":91,"ticker":"PFSA"}],"ok":true,"partial":31,"period":"Sep 21 \u2013 Sep 25, 2026","pumped":31,"signals":164,"slug":"weekly-2026-09-25"},"history":[{"date":"2026-08-22","hit_rate":14,"period":"Week ending Aug 22, 2026","pumped":16,"signals":117,"slug":"weekly-2026-08-22"},{"date":"2026-08-29","hit_rate":16,"period":"Week ending Aug 29, 2026","pumped":26,"signals":165,"slug":"weekly-2026-08-29"},{"date":"2026-09-05","hit_rate":21,"period":"Week ending Sep 05, 2026","pumped":22,"signals":106,"slug":"weekly-2026-09-05"},{"date":"2026-09-11","hit_rate":17,"period":"Sep 07 \u2013 Sep 11, 2026","pumped":14,"signals":82,"slug":"weekly-2026-09-11"},{"date":"2026-09-18","hit_rate":18,"period":"Sep 14 \u2013 Sep 18, 2026","pumped":19,"signals":108,"slug":"weekly-2026-09-18"},{"date":"2026-09-25","hit_rate":19,"period":"Sep 21 \u2013 Sep 25, 2026","pumped":31,"signals":164,"slug":"weekly-2026-09-25"}]};

const fmtHours = (h) => (h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`);

const WeeklyResultsBoard = () => {
  const [data, setData] = useState(WEEKLY_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/reports/weekly-summary?skip=0', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && d.ok && d.latest) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const w = data.latest;
  const total = w.signals || 1;
  const split = [
    { key: 'pumped', label: 'Hit +25%', n: w.pumped, bar: 'bg-emerald-400', text: 'text-emerald-300' },
    { key: 'partial', label: '+10 to 25%', n: w.partial, bar: 'bg-sky-400', text: 'text-sky-300' },
    { key: 'missed', label: 'Under +10%', n: w.missed, bar: 'bg-slate-600', text: 'text-slate-400' },
  ];
  const maxBucket = Math.max(...w.buckets.map((b) => Math.max(b.hit_rate, b.hit_rate_all_time)), 1);
  const hist = data.history || [];
  const maxHist = Math.max(...hist.map((h) => h.hit_rate), 1);

  return (
    <figure className="min-w-0 rounded-xl border border-[#1C3348] bg-[#07131F] overflow-hidden" aria-label="Published signal results">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1C3348] bg-[#0D1E2E]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-[#F0414F] flex-shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold text-[#DCE6F0] whitespace-nowrap">Signal results</span>
          <span className="hidden sm:inline text-xs text-slate-500 truncate">{w.period}</span>
        </div>
        <a href={`/reports/${w.slug}`} className="text-xs font-medium text-[#5AA9F0] hover:text-[#8CC4F5] whitespace-nowrap">
          Full report
        </a>
      </div>

      <div className="p-4 space-y-5">
        {/* Headline result + honest split */}
        <div>
          <p className="sm:hidden text-xs text-slate-500 mb-1">{w.period}</p>
          <div className="flex items-baseline gap-3">
            <span className="ts-num text-4xl font-semibold text-[#DCE6F0]">{w.hit_rate}%</span>
            <span className="text-sm text-slate-400 leading-snug">
              of <span className="ts-num text-[#DCE6F0]">{w.signals}</span> signals scored 85+ hit +25% within 3 trading days
            </span>
          </div>
          <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-[#1C3348]" aria-hidden="true">
            {split.map((x) => <span key={x.key} className={x.bar} style={{ width: `${(x.n / total) * 100}%` }} />)}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {split.map((x) => (
              <span key={x.key} className="inline-flex items-center gap-1.5 text-slate-400">
                <span className={`w-2 h-2 rounded-sm ${x.bar}`} />
                {x.label} <span className={`ts-num ${x.text}`}>{x.n}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Score buckets: does a higher score mean better odds? */}
        <div>
          <p className="text-xs text-slate-400 mb-2">Performance by score: this week vs. all-time</p>
          <div className="space-y-1.5">
            {w.buckets.map((b) => (
              <div key={b.band} className="grid grid-cols-[52px_1fr_88px] items-center gap-3">
                <span className="ts-num text-xs text-slate-300">{b.band}</span>
                <div className="space-y-1">
                  <span className="block h-1.5 rounded-full bg-[#5AA9F0]" style={{ width: `${(b.hit_rate / maxBucket) * 100}%` }} />
                  <span className="block h-1.5 rounded-full bg-slate-600" style={{ width: `${(b.hit_rate_all_time / maxBucket) * 100}%` }} />
                </div>
                <span className="ts-num text-xs text-right">
                  <span className="text-[#8CC4F5]">{b.hit_rate}%</span>
                  <span className="text-slate-500"> / {b.hit_rate_all_time}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Biggest movers after the signal */}
        {w.movers && w.movers.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-[#1C3348]">
                <th className="font-medium py-1.5">Biggest movers</th>
                <th className="font-medium py-1.5 text-right">Score</th>
                <th className="font-medium py-1.5 text-right">Peak</th>
                <th className="font-medium py-1.5 text-right">Time to peak</th>
              </tr>
            </thead>
            <tbody>
              {w.movers.map((m, i) => (
                <tr key={`${m.ticker}-${i}`} className="border-b border-[#1C3348]/60 last:border-0">
                  <td className="ts-num py-1.5 font-semibold text-[#DCE6F0]">{m.ticker}</td>
                  <td className="ts-num py-1.5 text-right text-slate-300">{m.score != null ? m.score : '–'}</td>
                  <td className="ts-num py-1.5 text-right text-emerald-300">+{m.peak_pct}%</td>
                  <td className="ts-num py-1.5 text-right text-slate-400">{fmtHours(m.hours_to_peak)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Recent weeks */}
        {hist.length > 1 && (
          <div>
            <p className="text-xs text-slate-400 mb-2">Hit rate, last {hist.length} weeks</p>
            <div className="flex items-end gap-2 h-12">
              {hist.map((h) => (
                <div key={h.slug} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${h.period}: ${h.hit_rate}% of ${h.signals}`}>
                  <span className="ts-num text-[10px] text-slate-400">{h.hit_rate}%</span>
                  <span
                    className={`w-full rounded-sm ${h.slug === w.slug ? 'bg-[#5AA9F0]' : 'bg-[#28445F]'}`}
                    style={{ height: `${Math.max(4, (h.hit_rate / maxHist) * 28)}px` }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <figcaption className="px-4 py-2.5 border-t border-[#1C3348] text-[11px] leading-relaxed text-slate-500">
        Every signal scored 85+ is counted, including the {w.missed} that moved less than 10%. Peak is the intraday high
        above the price when the score fired. Past results don't predict future returns.
      </figcaption>
    </figure>
  );
};

// ============================================================================
// WORKSPACES SHOWCASE (#features)
// One subscription, four workspaces. Mirrors the in-app terminal look
// (LiveWatch / ShortWatch / Replay share the same tokens): navy surfaces,
// IBM Plex Mono numbers, small colored tags, MACD dots. All numbers in the
// mocks are illustrative sample data, and say so.
// ============================================================================
const WS_CSS = `
.ws{--bg:#07131F;--surface:#0D1E2E;--raised:#13283C;--line:#1C3348;--line2:#28445F;--ink:#DCE6F0;--mute:#8197AD;--faint:#5A7189;
  --accent:#5AA9F0;--up:#2BC48A;--down:#F0414F;--warn:#F2B33D;--violet:#A77BF3;--orange:#F28C3D;
  --mono:'IBM Plex Mono',ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--ink)}
.ws .mono{font-family:var(--mono);font-variant-numeric:tabular-nums}
.ws .up{color:var(--up)} .ws .down{color:var(--down)} .ws .mute{color:var(--mute)} .ws .faint{color:var(--faint)}
.ws-tabs{display:flex;gap:0;border-bottom:1px solid var(--line2);overflow-x:auto;scrollbar-width:none}
.ws-tabs::-webkit-scrollbar{display:none}
@media (max-width:639px){.ws-tab small{display:none}.ws-tab{margin-right:20px}}
.ws-tab{flex:0 0 auto;display:flex;align-items:baseline;gap:10px;padding:14px 0 13px;margin-right:36px;border:0;border-bottom:2px solid transparent;margin-bottom:-1px;background:none;color:var(--mute);cursor:pointer;text-align:left;font:inherit;transition:color .15s}
.ws-tab:hover{color:var(--ink)}
.ws-tab[aria-selected="true"]{color:var(--ink);border-bottom-color:var(--ink)}
.ws-tab .ix{font:500 11px/1 var(--mono);color:var(--faint)}
.ws-tab b{font-size:16px;font-weight:600;line-height:1.2}
.ws-tab small{font-size:13px;color:var(--faint);line-height:1.3;white-space:nowrap}
.ws-panel{display:grid;grid-template-columns:minmax(0,5fr) minmax(0,7fr);gap:36px;align-items:center;margin-top:28px}
@media (max-width:1023px){.ws-panel{grid-template-columns:minmax(0,1fr);gap:24px}}
.ws-kick{font:500 12px/1 var(--mono);color:var(--faint)}
.ws-h{font-size:clamp(24px,3vw,32px);font-weight:600;letter-spacing:-.02em;line-height:1.12;margin:12px 0 10px;color:#fff}
.ws-p{font-size:15.5px;line-height:1.6;color:var(--mute);margin:0 0 20px}
.ws-pts{margin:0 0 24px;padding:0;list-style:none;border-top:1px solid var(--line)}
.ws-pt{display:flex;gap:14px;align-items:baseline;padding:12px 0;border-bottom:1px solid var(--line);font-size:14.5px;line-height:1.55;color:var(--mute)}
.ws-pt b{color:var(--ink);font-weight:600}
.ws-pt .n{flex:none;width:18px;font:500 11px/1 var(--mono);color:var(--faint)}
.ws-cta{display:flex;flex-wrap:wrap;gap:14px;align-items:center}
.ws-cta a{font-size:13.5px;font-weight:600;color:var(--accent);text-decoration:none;display:inline-flex;align-items:center;gap:4px}
.ws-cta a:hover{color:#8CC4F5}
.ws-cta span{font-size:12px;color:var(--faint);display:inline-flex;align-items:center;gap:5px}
/* window chrome */
.ws-win{position:relative;border:1px solid var(--line2);border-radius:6px;background:var(--bg);overflow:hidden}
.ws-bar{position:relative;display:flex;align-items:center;gap:10px;padding:9px 14px;background:var(--surface);border-bottom:1px solid var(--line);font-size:12px}

.ws-bar h5{margin:0;white-space:nowrap;font-size:13px;font-weight:700;display:flex;align-items:center;gap:7px;color:var(--ink)}
.ws-pg{white-space:nowrap;font:700 9.5px/1 var(--mono);letter-spacing:.08em;text-transform:uppercase;color:var(--c);border:1px solid color-mix(in srgb,var(--c) 45%,transparent);border-radius:5px;padding:3px 6px}
.ws-live{margin-left:auto;display:inline-flex;align-items:center;gap:6px;font:600 10.5px/1 var(--mono);color:var(--up)}
.ws-live i{width:6px;height:6px;border-radius:50%;background:var(--up)}
.ws-body{position:relative;padding:12px}
.ws-sample{margin-left:auto;font:500 9.5px/1 var(--mono);color:var(--faint);letter-spacing:.04em}
.ws-sample+.ws-live{margin-left:10px}
@media (max-width:639px){.ws-sample{display:none}.ws-sample+.ws-live{margin-left:auto}}
/* shared mock atoms */
.ws-tag{display:inline-block;font:700 9px/1 var(--mono);padding:3px 5px;border-radius:4px;border:1px solid;letter-spacing:.03em;white-space:nowrap}
.ws-barx{display:inline-block;height:4px;border-radius:2px;background:var(--line2);position:relative;overflow:hidden;vertical-align:middle}
.ws-barx i{position:absolute;left:0;top:0;bottom:0;border-radius:2px}
.ws-pill{font:600 11px/1 var(--mono);padding:3px 6px;border-radius:5px;white-space:nowrap}
.ws-pill.up{color:var(--up);background:color-mix(in srgb,var(--up) 14%,transparent)}
.ws-pill.down{color:var(--down);background:color-mix(in srgb,var(--down) 14%,transparent)}
.ws-md{display:inline-flex;gap:3px}.ws-md i{width:7px;height:7px;border-radius:50%}
.ws-md i.ring{box-shadow:0 0 0 1.5px var(--bg),0 0 0 3px var(--warn)}
.ws-chips{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
.ws-chip{font-size:11px;padding:4px 9px;border-radius:999px;border:1px solid var(--line2);color:var(--mute);white-space:nowrap}
.ws-chip.on{border-color:var(--c);color:var(--ink);background:color-mix(in srgb,var(--c) 14%,transparent)}
.ws-card{background:var(--surface);border:1px solid var(--line);border-radius:4px;padding:10px}
.ws-card h6{margin:0 0 8px;font:700 9.5px/1 var(--mono);letter-spacing:.08em;color:var(--faint);display:flex;justify-content:space-between;align-items:center;text-transform:uppercase}
/* live watch */
.lw-grid{display:grid;grid-template-columns:minmax(0,1fr) 200px;gap:10px}
@media (max-width:639px){.lw-grid{grid-template-columns:minmax(0,1fr)}.lw-side{display:none}}
.lw-row{display:grid;grid-template-columns:48px minmax(0,1fr) 62px 54px 64px 22px;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid var(--line);font-size:11.5px;white-space:nowrap}
.lw-row.sel{background:var(--raised)}
.lw-row .tk{font-weight:700;font-size:12.5px}
.lw-row .tags{display:flex;gap:4px;overflow:hidden}
.lw-row .al{font:700 10px/1 var(--mono);text-align:center;color:var(--warn)}
.lw-flash{color:var(--up)}
.lw-tabs{display:flex;gap:2px;border-bottom:1px solid var(--line);margin:10px 0 8px}
.lw-tabs span{padding:6px 4px;font-size:10px;color:var(--mute)}
.lw-tabs span.on{color:var(--ink);box-shadow:inset 0 -2px 0 var(--accent)}
.lw-plan{position:relative;height:8px;border-radius:4px;background:linear-gradient(90deg,var(--down) 0 18%,var(--accent) 18% 46%,var(--up) 46% 100%);opacity:.85;margin:16px 0 6px}
.lw-plan b{position:absolute;top:-14px;transform:translateX(-50%);font:700 9px/1 var(--mono);color:var(--ink)}
.lw-plan em{position:absolute;top:-4px;width:2px;height:16px;background:#fff;border-radius:1px}
.lw-plan-l{display:flex;justify-content:space-between;font:600 9px/1 var(--mono);color:var(--faint)}
/* short watch */
.sw-row{display:grid;grid-template-columns:minmax(0,1.5fr) 40px 62px 50px 74px 50px 48px;align-items:center;gap:8px;padding:8px 10px;border-bottom:1px solid var(--line);font-size:11.5px;white-space:nowrap}
@media (max-width:639px){.sw-row{grid-template-columns:minmax(0,1.5fr) 40px 62px 50px}.sw-row .hx{display:none}}
.sw-hd{background:none;border-color:transparent;padding-top:0;padding-bottom:2px;font:600 9px/1 var(--mono);letter-spacing:.06em;color:var(--faint);text-transform:uppercase}
.sw-stage{display:flex;flex-direction:column;gap:3px;min-width:0}
.sw-stage .st{white-space:nowrap;overflow:hidden;display:inline-flex;align-items:center;gap:5px;font:700 10px/1 var(--mono);letter-spacing:.03em}
.sw-stage .st i{flex:none;width:16px;height:16px;border-radius:50%;display:grid;place-items:center;font-style:normal;font-size:9px;color:#07131F}
.sw-stage small{font-size:10px;color:var(--faint);overflow:hidden;text-overflow:ellipsis}
.sw-steps{display:flex;gap:4px;margin-top:10px}
.sw-steps span{flex:1;min-width:0;padding:7px 8px;border-radius:3px;border:1px solid var(--line);background:var(--surface);font-size:10px;color:var(--mute);display:flex;justify-content:space-between;gap:6px}
.sw-steps span b{font-family:var(--mono);color:var(--ink)}
.sw-steps span.on{border-color:var(--up);color:var(--ink);background:color-mix(in srgb,var(--up) 10%,var(--surface))}
/* replay */
.rp-grid{display:grid;grid-template-columns:minmax(0,1fr) 170px;gap:10px}
@media (max-width:639px){.rp-grid{grid-template-columns:minmax(0,1fr)}.rp-side{display:none}}
.rp-ctrl{display:flex;align-items:center;gap:8px;margin-top:8px}
.rp-btn{width:28px;height:28px;border-radius:7px;display:grid;place-items:center;background:var(--raised);border:1px solid var(--line2);color:var(--ink)}
.rp-btn.pri{background:var(--c);border-color:var(--c);color:#07131F}
.rp-scrub{flex:1;height:6px;border-radius:3px;background:var(--line2);position:relative}
.rp-scrub i{position:absolute;left:0;top:0;bottom:0;width:58%;border-radius:3px;background:var(--c)}
.rp-scrub em{position:absolute;left:58%;top:50%;width:12px;height:12px;border-radius:50%;background:#fff;transform:translate(-50%,-50%);box-shadow:0 0 0 3px color-mix(in srgb,var(--c) 40%,transparent)}
.rp-seg{display:inline-flex;border:1px solid var(--line2);border-radius:7px;overflow:hidden}
.rp-seg span{font:600 10px/1 var(--mono);padding:6px 7px;color:var(--mute);border-right:1px solid var(--line2)}
.rp-seg span:last-child{border-right:0}.rp-seg span.on{background:var(--c);color:#07131F}
.rp-trade{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:8px}
.rp-trade span{padding:8px;border-radius:7px;text-align:center;font:700 11px/1 var(--mono);letter-spacing:.04em}
/* scalp */
.sc-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px}
@media (max-width:639px){.sc-grid{grid-template-columns:minmax(0,1fr)}}
.sc-sig{display:flex;align-items:center;justify-content:space-between;padding:12px;border-radius:4px;border:1px solid color-mix(in srgb,var(--up) 50%,transparent);background:color-mix(in srgb,var(--up) 10%,var(--surface))}
.sc-sig b{font:700 18px/1 var(--mono);color:var(--up);letter-spacing:.02em}
.sc-cock{display:grid;grid-template-columns:repeat(6,1fr);gap:6px;text-align:center}
.sc-cock i{display:block;width:26px;height:26px;margin:0 auto 4px;border-radius:50%;border:3px solid}
.sc-cock span{font:600 9.5px/1 var(--mono);color:var(--faint)}
.sc-l2{display:grid;grid-template-columns:1fr 1fr;gap:4px;font:500 10.5px/1 var(--mono)}
.sc-l2 div{position:relative;display:flex;justify-content:space-between;padding:5px 7px;border-radius:4px;overflow:hidden}
.sc-l2 div em{position:absolute;top:0;bottom:0;opacity:.18}
.sc-l2 .b em{right:0;background:var(--up)} .sc-l2 .a em{left:0;background:var(--down)}
.sc-l2 .wall{outline:1px solid var(--warn);outline-offset:-1px}
.sc-voice{display:flex;align-items:center;gap:8px;padding:9px 10px;border-radius:4px;background:var(--surface);border:1px solid var(--line);font-size:11.5px;color:var(--mute)}
/* core grid */
.ws-core{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0 40px;border-top:1px solid var(--line2);margin-top:72px}
@media (max-width:1023px){.ws-core{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:639px){.ws-core{grid-template-columns:minmax(0,1fr)}}
.ws-core>div{padding:20px 0;border-bottom:1px solid var(--line)}
.ws-core h4{margin:0 0 4px;font-size:15px;font-weight:600;color:var(--ink)}
.ws-core p{margin:0;font-size:13.5px;line-height:1.55;color:var(--mute)}
.ws-classic{margin-top:14px;display:flex;flex-wrap:wrap;gap:8px 18px;align-items:center;justify-content:flex-start;font-size:12.5px;color:var(--faint)}
.ws-classic b{color:var(--mute);font-weight:600}
@media (prefers-reduced-motion:reduce){.ws *{animation:none!important}}
`;

const Tag = ({ t, c }) => <span className="ws-tag" style={{ color: c, borderColor: `color-mix(in srgb,${c} 45%,transparent)` }}>{t}</span>;
const MiniBar = ({ pct, c, w = 46 }) => <span className="ws-barx" style={{ width: w }}><i style={{ width: `${pct}%`, background: c }} /></span>;
const MacdDots = ({ s }) => (
  <span className="ws-md">
    {s.split('').map((ch, i) => (
      <i key={i} className={ch === 'G' || ch === 'R' ? 'ring' : ''} style={{ background: ch.toLowerCase() === 'g' ? 'var(--up)' : 'var(--down)' }} />
    ))}
  </span>
);
const WinBar = ({ page, live = true }) => (
  <div className="ws-bar">
    <h5>TunaSignal AI <span className="ws-pg">{page}</span></h5>
    <span className="ws-sample">sample data</span>
    {live ? <span className="ws-live"><i />Live</span> : <span className="ws-live" style={{ color: 'var(--mute)' }}>Historical · NY time</span>}
  </div>
);

const LiveWatchMock = () => (
  <div className="ws-win">
    <WinBar page="Live Watch" />
    <div className="ws-body">
      <div className="ws-chips">
        {['All', '85+', 'Has alerts 1h', 'Under $5'].map((c, i) => <span key={c} className={`ws-chip ${i === 1 ? 'on' : ''}`}>{c}</span>)}
      </div>
      <div className="lw-grid">
        <div>
          {[
            { tk: 'ABCX', tags: [['MACD 2m', 'var(--accent)'], ['T2', 'var(--violet)']], sc: 94, px: '$3.42', pct: 18.6, md: 'gGgggggr', al: 7, sel: true, flash: true },
            { tk: 'QRVO', tags: [['VOL 5m', 'var(--warn)'], ['DIL', 'var(--down)']], sc: 88, px: '$1.17', pct: 9.4, md: 'ggggrrgg', al: 4 },
            { tk: 'MNTL', tags: [['SEC 1h', 'var(--violet)']], sc: 81, px: '$6.05', pct: 4.1, md: 'gggrrrgg', al: 2 },
            { tk: 'PLZA', tags: [['NEWS', 'var(--orange)']], sc: 72, px: '$0.84', pct: -2.3, md: 'rrgggrrr', al: 1 },
            { tk: 'KYTE', tags: [['MACD 15m', 'var(--accent)']], sc: 66, px: '$2.21', pct: 1.2, md: 'grgrggrr', al: 0 },
          ].map((r) => (
            <div key={r.tk} className={`lw-row ${r.sel ? 'sel' : ''}`}>
              <span className="tk">{r.tk}</span>
              <span className="tags">{r.tags.map(([t, c]) => <Tag key={t} t={t} c={c} />)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <MiniBar pct={r.sc} c={r.sc >= 85 ? 'var(--violet)' : r.sc >= 70 ? 'var(--up)' : 'var(--warn)'} w={22} />
                <b className="mono" style={{ color: r.sc >= 85 ? 'var(--violet)' : r.sc >= 70 ? 'var(--up)' : 'var(--warn)', fontSize: 11 }}>{r.sc}%</b>
              </span>
              <span className={`mono ${r.flash ? 'lw-flash' : ''}`}>{r.px}</span>
              <span><span className={`ws-pill ${r.pct >= 0 ? 'up' : 'down'}`}>{r.pct >= 0 ? '+' : ''}{r.pct}%</span></span>
              <span className="al">{r.al || '·'}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 4px 0', fontSize: 10.5 }} className="faint">
            <span>MACD 1m → 1M <MacdDots s="gGgggggr" /></span>
            <span className="mono">prices 5s · alerts 20s</span>
          </div>
        </div>
        <div className="lw-side ws-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="44" height="44" viewBox="0 0 46 46" aria-hidden="true">
              <circle cx="23" cy="23" r="19" fill="none" stroke="var(--line2)" strokeWidth="4" />
              <circle cx="23" cy="23" r="19" fill="none" stroke="var(--violet)" strokeWidth="4" strokeDasharray={`${0.94 * 119} 119`} transform="rotate(-90 23 23)" strokeLinecap="round" />
              <text x="23" y="27.5" textAnchor="middle" fontSize="12.5" fontWeight="700" fill="var(--violet)" fontFamily="var(--mono)">94</text>
            </svg>
            <div style={{ minWidth: 0 }}>
              <b style={{ fontSize: 14 }}>ABCX</b>
              <div className="mono up" style={{ fontSize: 11 }}>$3.42 +18.6%</div>
            </div>
          </div>
          <div className="lw-tabs">{['Overview', 'Alerts', 'Chart', 'Details'].map((t, i) => <span key={t} className={i ? '' : 'on'}>{t}</span>)}</div>
          <h6 style={{ margin: '0 0 2px', font: '700 9px/1 var(--mono)', letterSpacing: '.08em', color: 'var(--faint)' }}>TRADE PLAN</h6>
          <div className="lw-plan">
            <b style={{ left: '56%' }}>now</b><em style={{ left: '56%' }} />
          </div>
          <div className="lw-plan-l"><span className="down">stop</span><span style={{ color: 'var(--accent)' }}>buy</span><span className="up">T1 T2 T3</span></div>
          <div style={{ marginTop: 12, display: 'grid', gap: 6, fontSize: 10.5 }}>
            {[['Dilution', 31, 'var(--accent)', 'MODERATE'], ['Squeeze', 51, 'var(--warn)', 'MODERATE']].map(([l, p, c, t]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <span className="mute">{l}</span><MiniBar pct={p} c={c} w={48} /><span className="mono" style={{ color: c, fontSize: 9.5, fontWeight: 700 }}>{t}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 8px', borderRadius: 7, background: 'var(--raised)', fontSize: 10.5 }}>
            <span><Bell className="w-3 h-3 inline mr-1" style={{ color: 'var(--accent)' }} />Telegram · 4h</span>
            <span style={{ width: 26, height: 14, borderRadius: 7, background: 'var(--accent)', position: 'relative' }}><i style={{ position: 'absolute', right: 2, top: 2, width: 10, height: 10, borderRadius: '50%', background: '#fff' }} /></span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const STAGE_C = { 4: 'var(--up)', 3: 'var(--accent)', 2: 'var(--warn)', 1: 'var(--orange)', 0: 'var(--faint)' };
const ShortWatchMock = () => (
  <div className="ws-win">
    <WinBar page="Short Watch" />
    <div className="ws-body">
      <div className="sw-row sw-hd">
        <span>Stage</span><span>Score</span><span>Live %</span><span>Off high</span><span className="hx">Borrow</span><span className="hx">Squeeze</span><span className="hx">R:R</span>
      </div>
      {[
        { tk: 'FADE', st: 4, lbl: 'SETUP', why: 'all checks pass · score 78', sc: 78, pct: 64.2, off: -14.8, br: ['EASY', '38%'], sq: ['LOW', 'var(--up)'], rr: '2.4' },
        { tk: 'DRPX', st: 3, lbl: 'EXECUTABLE', why: 'score 58, needs 65+', sc: 58, pct: 47.9, off: -9.1, br: ['MOD', '112%'], sq: ['MED', 'var(--warn)'], rr: '1.6' },
        { tk: 'SPKE', st: 2, lbl: 'EXHAUSTION', why: 'borrow HARD · not shortable', sc: 61, pct: 88.0, off: -11.3, br: ['HARD', '363%'], sq: ['MED', 'var(--warn)'], rr: '—' },
        { tk: 'RKET', st: 1, lbl: 'PUMP', why: 'only 1 reversal signal', sc: 34, pct: 52.6, off: -3.9, br: ['EASY', '22%'], sq: ['HIGH', 'var(--down)'], rr: '—' },
        { tk: 'MOON', st: 0, lbl: 'STILL RISING', why: 'within 3% of the high', sc: 12, pct: 121.4, off: -0.8, br: ['MOD', '96%'], sq: ['HIGH', 'var(--down)'], rr: '—' },
      ].map((r) => (
        <div key={r.tk} className="sw-row" style={r.st === 4 ? { borderColor: 'color-mix(in srgb,var(--up) 55%,transparent)' } : undefined}>
          <span className="sw-stage">
            <span className="st" style={{ color: STAGE_C[r.st] }}>
              <b style={{ color: 'var(--ink)', marginRight: 3, fontSize: 11.5 }}>{r.tk}</b>
              <i style={{ background: STAGE_C[r.st] }}>{r.st || '↑'}</i>{r.lbl}
            </span>
            <small>{r.why}</small>
          </span>
          <b className="mono" style={{ color: r.sc >= 65 ? 'var(--up)' : 'var(--mute)' }}>{r.sc}</b>
          <span><span className="ws-pill up">+{r.pct}%</span></span>
          <span className="mono" style={{ color: r.off <= -10 ? 'var(--down)' : 'var(--mute)' }}>{r.off}%</span>
          <span className="hx mono" style={{ fontSize: 10.5 }}><b style={{ color: r.br[0] === 'HARD' ? 'var(--down)' : 'var(--ink)' }}>{r.br[0]}</b> <span className="faint">{r.br[1]}</span></span>
          <span className="hx"><Tag t={r.sq[0]} c={r.sq[1]} /></span>
          <span className="hx mono" style={{ color: r.rr !== '—' && parseFloat(r.rr) >= 2 ? 'var(--up)' : 'var(--mute)' }}>{r.rr}</span>
        </div>
      ))}
      <div className="sw-steps">
        {[['Pump', 14], ['Exhaustion', 9], ['Executable', 5], ['Setups', 2]].map(([l, n], i) => (
          <span key={l} className={i === 3 ? 'on' : ''}>{i + 1} {l}<b>{n}</b></span>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 10.5 }} className="faint mono">backtest · short 30%+ gainers at close, cover next day · borrowable only</div>
    </div>
  </div>
);

const ReplayMock = () => {
  // Deterministic candles: a quiet base, the alert, a pump, then hidden future.
  const closes = [40, 41, 40, 42, 41, 43, 42, 44, 52, 58, 55, 63, 70, 66, 74, 80, 76, 72, 78, 75, 70, 68, 72, 66];
  const cursor = 15;
  const W = 380, H = 150, bw = W / closes.length;
  const y = (v) => H - 14 - ((v - 34) / (84 - 34)) * (H - 28);
  return (
    <div className="ws-win">
      <WinBar page="Replay" live={false} />
      <div className="ws-body">
        <div className="rp-grid">
          <div className="ws-card" style={{ padding: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 11 }}>
              <span><b>Mystery ticker</b> <span className="faint">· score 92 at alert</span></span>
              <span className="rp-seg">{['1m', '5m', '15m'].map((t) => <span key={t} className={t === '5m' ? 'on' : ''}>{t}</span>)}</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Replay chart: candles up to the cursor, the rest hidden">
              <line x1="0" x2={W} y1={y(55)} y2={y(55)} stroke="var(--up)" strokeDasharray="4 4" strokeOpacity=".6" />
              <text x="4" y={y(55) - 4} fontSize="9" fill="var(--up)" fontFamily="var(--mono)">+25% from alert</text>
              {closes.map((c, i) => {
                const o = i ? closes[i - 1] : c - 1;
                const hi = Math.max(o, c) + 2, lo = Math.min(o, c) - 2;
                const shown = i <= cursor;
                const col = c >= o ? 'var(--up)' : 'var(--down)';
                const x = i * bw + bw / 2;
                return shown ? (
                  <g key={i}>
                    <line x1={x} x2={x} y1={y(hi)} y2={y(lo)} stroke={col} />
                    <rect x={x - bw * 0.32} width={bw * 0.64} y={y(Math.max(o, c))} height={Math.max(2, Math.abs(y(o) - y(c)))} fill={col} rx="1" />
                  </g>
                ) : null;
              })}
              <rect x={(cursor + 1) * bw} y="0" width={W - (cursor + 1) * bw} height={H} fill="var(--raised)" opacity=".55" />
              <text x={(cursor + 1) * bw + (W - (cursor + 1) * bw) / 2} y={H / 2 + 4} textAnchor="middle" fontSize="11" fill="var(--faint)" fontFamily="var(--mono)">no look-ahead</text>
              <line x1={8 * bw + bw / 2} x2={8 * bw + bw / 2} y1="6" y2={H - 4} stroke="var(--warn)" strokeDasharray="2 3" />
              <circle cx={8 * bw + bw / 2} cy="10" r="6" fill="var(--warn)" />
              <text x={8 * bw + bw / 2} y="13" textAnchor="middle" fontSize="8" fontWeight="700" fill="#07131F">A</text>
              <circle cx={10 * bw + bw / 2} cy={y(55) + 12} r="7" fill="var(--accent)" />
              <text x={10 * bw + bw / 2} y={y(55) + 15} textAnchor="middle" fontSize="8" fontWeight="700" fill="#07131F">B</text>
            </svg>
            <div className="rp-ctrl">
              <span className="rp-btn" aria-hidden="true"><ChevronLeft className="w-3.5 h-3.5" /></span>
              <span className="rp-btn pri" aria-hidden="true"><PlayCircle className="w-4 h-4" /></span>
              <span className="rp-btn" aria-hidden="true"><ChevronRight className="w-3.5 h-3.5" /></span>
              <span className="rp-scrub"><i /><em /></span>
              <span className="rp-seg">{['1x', '4x', '16x'].map((t) => <span key={t} className={t === '4x' ? 'on' : ''}>{t}</span>)}</span>
            </div>
          </div>
          <div className="rp-side" style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
            <span className="rp-seg" style={{ justifySelf: 'start' }}><span>Watch</span><span className="on">Practice</span></span>
            <div className="ws-card">
              <h6>Your trade</h6>
              <div style={{ display: 'grid', gap: 5, fontSize: 11 }}>
                <span style={{ display: 'flex', justifyContent: 'space-between' }}><span className="mute">Bought</span><b className="mono">$0.55</b></span>
                <span style={{ display: 'flex', justifyContent: 'space-between' }}><span className="mute">Now</span><b className="mono">$0.80</b></span>
                <span style={{ display: 'flex', justifyContent: 'space-between' }}><span className="mute">P/L</span><b className="mono up">+45.5%</b></span>
              </div>
              <div className="rp-trade">
                <span style={{ background: 'var(--raised)', color: 'var(--faint)' }}>BUY</span>
                <span style={{ background: 'var(--down)', color: '#fff' }}>SELL</span>
              </div>
            </div>
            <div className="ws-card" style={{ fontSize: 10.5 }}>
              <h6>Pump-hit <span className="mono up">HIT</span></h6>
              <span className="mute">+25% within 3 days of the alert</span>
            </div>
          </div>
        </div>
        </div>
    </div>
  );
};

const ScalpWatchMock = () => (
  <div className="ws-win">
    <WinBar page="ScalpWatch" />
    <div className="ws-body">
      <div className="sc-grid">
        <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          <div className="sc-sig">
            <div>
              <div className="faint" style={{ fontSize: 10.5, marginBottom: 6 }}>ABCX · AI signal</div>
              <b>STRONG BUY</b>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="mono" style={{ fontSize: 16, fontWeight: 700 }}>$3.4215</div>
              <div className="mono faint" style={{ fontSize: 10 }}>live · up to 20 updates/sec</div>
            </div>
          </div>
          <div className="ws-card">
            <h6>MACD cockpit</h6>
            <div className="sc-cock">
              {[['1m', 'var(--up)'], ['5m', 'var(--up)'], ['15m', 'var(--up)'], ['1H', 'var(--warn)'], ['4H', 'var(--up)'], ['1D', 'var(--down)']].map(([t, c]) => (
                <div key={t}><i style={{ borderColor: c, background: `color-mix(in srgb,${c} 18%,transparent)` }} /><span>{t}</span></div>
              ))}
            </div>
          </div>
          <div className="sc-voice">
            <Volume2 className="w-4 h-4" style={{ color: 'var(--violet)' }} />
            <span>"ABCX, strong buy. MACD turning up on five minutes."</span>
          </div>
        </div>
        <div className="ws-card">
          <h6>Level 2 <span className="mono" style={{ color: 'var(--warn)' }}>wall 3.45</span></h6>
          <div className="sc-l2">
            {[
              ['3.42', '4,200', 70, '3.43', '1,800', 30],
              ['3.41', '9,600', 100, '3.44', '2,400', 38],
              ['3.40', '3,100', 52, '3.45', '22,000', 100, true],
              ['3.39', '2,700', 44, '3.46', '1,200', 22],
              ['3.38', '5,800', 80, '3.47', '900', 16],
            ].map(([bp, bs, bw, ap, as, aw, wall]) => (
              <React.Fragment key={bp}>
                <div className="b"><em style={{ width: `${bw}%` }} /><span className="up">{bp}</span><span>{bs}</span></div>
                <div className={`a ${wall ? 'wall' : ''}`}><em style={{ width: `${aw}%` }} /><span className="down">{ap}</span><span>{as}</span></div>
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 10.5 }}>
            <span className="mute">Order-flow imbalance</span><b className="mono up">+0.42</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 10.5 }}>
            <span className="mute">Spread</span><b className="mono">0.29%</b>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const WORKSPACES = [
  {
    id: 'live', name: 'Live Watch', sub: 'Find & track setups', icon: Activity, c: '#5AA9F0', isNew: true,
    kicker: 'For the long side', title: 'Your watchlist, alerts and research on one screen',
    text: 'Every stock receives a score from 0–100, updated live. Click a row and its whole story opens beside the list — no page hopping, no lost place.',
    pts: [
      [<b key="b">Read a row in one glance:</b>, ' setup tags, score, live price and %, volume, MACD from 1m to 1M, dilution flags and alerts in the last hour.'],
      [<b key="b">Ticker panel:</b>, ' 24-hour alert activity, the trade plan (stop, buy zone, targets), candles with VWAP, RSI and MACD, plus dilution and squeeze risk with filing dates.'],
      [<b key="b">One-click Telegram alerts</b>, ' for a single ticker — on for 4 hours by default, then they switch themselves off.'],
      [<b key="b">Live feed, sound alerts and four layouts:</b>, ' docked panel, inline, feed or focus. Prices every 5 seconds, alerts every 20.'],
    ],
    learn: '/learn#livewatch', mock: LiveWatchMock,
  },
  {
    id: 'short', name: 'Short Watch', sub: 'Identify potential fade setups', icon: TrendingDown, c: '#F0414F', isNew: true,
    kicker: 'For fading pumps', title: 'Can I short this now — and if not, why?',
    text: "Today's biggest gainers, checked for a fade instead of a pump. Every row says which stage it reached and the exact reason it stopped there.",
    pts: [
      [<b key="b">Four stages:</b>, ' pump detected → exhaustion confirmed → executable → setup. Only stage 4 is tradeable; it never shorts a stock making new highs.'],
      [<b key="b">Borrow and squeeze come first:</b>, ' borrow tier and yearly fee, data freshness, and a squeeze-risk read that blocks the trade when it is HIGH.'],
      [<b key="b">A short plan with real costs:</b>, ' entry below VWAP, a stop above the high, targets, and reward-to-risk after borrow and trading costs.'],
      [<b key="b">The backtest is the track record:</b>, ' shorting 30%+ gainers at the close and covering next day, with tunable filters and weights.'],
    ],
    learn: '/learn#shortwatch', mock: ShortWatchMock,
  },
  {
    id: 'replay', name: 'Replay', sub: 'Rehearse past alerts', icon: RotateCcw, c: '#A77BF3', isNew: true,
    kicker: 'For practising', title: 'Play back any past alert, bar by bar',
    text: 'See exactly what the chart looked like when an alert fired — and what happened next. Then trade it blind to test your own timing.',
    pts: [
      [<b key="b">Watch mode:</b>, ' scrub, step and play at 1m, 5m or 15m, from the alert up to the last trade. The chart only shows bars up to the cursor, with the score and plan frozen at alert time.'],
      [<b key="b">Practice mode:</b>, ' a random past alert with the ticker hidden. Buy and sell when you would; the outcome is revealed at the end.'],
      [<b key="b">Filter the library</b>, ' by score band (75+, 85+, 90+, 95+) and outcome, pump hits and misses alike.'],
      [<b key="b">Honest scoring:</b>, ' a pump hit means +25% within 3 days of the alert. You can keep playing past that, but later moves never count as a hit.'],
    ],
    learn: '/learn#replay', mock: ReplayMock,
  },
  {
    id: 'scalp', name: 'ScalpWatch', sub: 'Trade it live', icon: Crosshair, c: '#2BC48A', isNew: false,
    kicker: 'For intraday scalping', title: 'A fast cockpit for scalping',
    text: 'Live prices streamed over WebSocket, seven AI signal types and a six-timeframe MACD cockpit, with voice alerts so you can keep your eyes on the chart.',
    pts: [
      [<b key="b">7 AI signals:</b>, ' STRONG BUY, BUY, SCALP, HOLD, TAKE PROFIT, SELL and WATCHING, from 200+ features across 6 timeframes.'],
      [<b key="b">Level 2 order book</b>, ' with wall detection, spread, order-flow imbalance and breakout probability.'],
      [<b key="b">Hands-free voice alerts</b>, ' for signal changes, MACD turns, breakouts, volume spikes and your own price targets.'],
      [<b key="b">Auto watchlist:</b>, ' 5%+ gainers added automatically, with dilution and halt monitoring.'],
    ],
    learn: '/learn#scalpwatch', mock: ScalpWatchMock,
  },
];

const CORE = [
  { icon: Brain, t: 'TunaSignalAI Score', d: 'One 0–100 analytical rating from MACD across timeframes from 1 minute to 1 month, relative volume, SEC filings, news and fundamentals.' },
  { icon: Zap, t: 'Alerts as they fire', d: 'MACD crossovers, volume spikes, score changes, SEC filings and news — on screen, by sound, Telegram or email.' },
  { icon: Shield, t: 'Risk you can see', d: 'Dilution, offering ability, warrants, splits, short squeeze and financial health, with dates and sources.' },
  { icon: Target, t: 'A plan on every setup', d: 'Buy zone, stop and targets on the long side; entry, stop and costed R:R on the short side.' },
  { icon: BarChart3, t: 'Unlimited coverage', d: 'Thousands of US stocks monitored, advanced filters, and every past signal published weekly — misses included.' },
  { icon: RefreshCw, t: 'Re-run on demand', d: 'Hit refresh on any ticker to re-run the full analysis now, and sync your picks to Webull with the extension.' },
];

const WorkspacesShowcase = ({ onStart }) => {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const ws = WORKSPACES[active];
  const Mock = ws.mock;

  const onKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const n = (active + (e.key === 'ArrowRight' ? 1 : -1) + WORKSPACES.length) % WORKSPACES.length;
    setActive(n);
    tabRefs.current[n]?.focus();
  };

  return (
    <div className="ws">
      <style>{WS_CSS}</style>
      <div role="tablist" aria-label="TunaSignal AI workspaces" className="ws-tabs" onKeyDown={onKey}>
        {WORKSPACES.map((w, i) => (
          <button
            key={w.id}
            ref={(el) => { tabRefs.current[i] = el; }}
            role="tab"
            id={`ws-tab-${w.id}`}
            aria-selected={active === i}
            aria-controls={`ws-panel-${w.id}`}
            tabIndex={active === i ? 0 : -1}
            className="ws-tab"
            style={{ '--c': w.c }}
            onClick={() => setActive(i)}
          >
            <span className="ix">0{i + 1}</span>
            <b>{w.name}</b>
            <small>{w.sub}</small>
          </button>
        ))}
      </div>

      <div
        key={ws.id}
        role="tabpanel"
        id={`ws-panel-${ws.id}`}
        aria-labelledby={`ws-tab-${ws.id}`}
        className="ws-panel"
        style={{ '--c': ws.c }}
      >
        <div>
          <div className="ws-kick">{ws.kicker}</div>
          <h3 className="ws-h">{ws.title}</h3>
          <p className="ws-p">{ws.text}</p>
          <ul className="ws-pts">
            {ws.pts.map((p, i) => (
              <li key={i} className="ws-pt"><span className="n">{i + 1}</span><span>{p}</span></li>
            ))}
          </ul>
          <div className="ws-cta">
            <a href={ws.learn}>Read the {ws.name} guide</a>
            <button
              type="button"
              onClick={onStart}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-semibold bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5] transition-colors"
            >
              Try it free
            </button>
            <span>Included in every plan</span>
          </div>
        </div>
        <Mock />
      </div>

      <div className="ws-core">
        {CORE.map((f) => (
          <div key={f.t}>
            <h4>{f.t}</h4><p>{f.d}</p>
          </div>
        ))}
      </div>
      <p className="ws-classic">
        <span>Prefer the classic pages? <b>Stock Watch</b> and <b>Alerts</b> are still in the menu.</span>
        <span>· Telegram &amp; email alerts · Webull Watchlist Sync · Web &amp; mobile</span>
      </p>
    </div>
  );
};

const LandingPage = () => {
  // Terminal identity: the landing page always renders dark, whatever the
  // app-wide theme setting is (that still applies inside the app).
  useTheme();
  const darkMode = true;
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Announcement bar for the Webull Sync extension. Dismissal persists so
  // returning visitors aren't nagged, but new visitors always see it.
  const [showExtBanner, setShowExtBanner] = useState(() => {
    try {
      return localStorage.getItem('webull_ext_banner_dismissed') !== '1';
    } catch (e) {
      return true;
    }
  });

  const dismissExtBanner = () => {
    setShowExtBanner(false);
    try {
      localStorage.setItem('webull_ext_banner_dismissed', '1');
    } catch (e) {
      /* storage unavailable — banner simply returns next visit */
    }
  };

  const [showVideoModal, setShowVideoModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Show a one-click "Sign in" prompt right after logout (?from_logout=true),
  // then strip the param so it doesn't linger on refresh/share.
  const [showLogoutToast, setShowLogoutToast] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('from_logout') === 'true') {
      setShowLogoutToast(true);
      params.delete('from_logout');
      params.delete('message');
      const qs = params.toString();
      window.history.replaceState({}, '', qs ? `/?${qs}` : '/');
    }
  }, [location.search]);

  // Auto-dismiss so it never lingers over content.
  useEffect(() => {
    if (!showLogoutToast) return;
    const t = setTimeout(() => setShowLogoutToast(false), 6000);
    return () => clearTimeout(t);
  }, [showLogoutToast]);

  // "How it works" onboarding overlay. Auto-opens once for first-time visitors
  // (respects the dismissal flag), and can be reopened any time from the header /
  // hero "How it works" button. A short delay lets the hero paint first so the
  // modal feels like a welcome rather than a blocking interstitial.
  const AUTO_SHOW_TOUR = true;
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (!AUTO_SHOW_TOUR) return;
    let t;
    try {
      if (!localStorage.getItem(LANDING_TOUR_STORAGE_KEY)) {
        t = setTimeout(() => setShowTour(true), 600);
      }
    } catch (e) { /* storage unavailable — skip */ }
    return () => { if (t) clearTimeout(t); };
  }, []);

  // YouTube video ID
  const youtubeVideoId = '5Pv3v_fqZQ4';

  // Score Lookup State
  const [scoreSearch, setScoreSearch] = useState('');
  const [scoreResult, setScoreResult] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreAnalyzing, setScoreAnalyzing] = useState(false);
  const [scoreError, setScoreError] = useState('');
  const [scoreNewlyAdded, setScoreNewlyAdded] = useState(false);
  const scoreInputRef = useRef(null);

  const handleScoreLookup = useCallback(async (tickerOverride) => {
    const ticker = (tickerOverride || scoreSearch).trim().toUpperCase();
    
    // Client-side validation
    if (!ticker) {
      setScoreError('Please enter a ticker symbol (e.g. AAPL, TSLA)');
      return;
    }
    if (!/^[A-Z]{1,5}([.-][A-Z]{1,2})?$/.test(ticker)) {
      setScoreError(`"${ticker}" doesn't look like a valid ticker symbol. US stock tickers are 1–5 letters (e.g. AAPL, TSLA, BRK.B).`);
      return;
    }

    setScoreLoading(true);
    setScoreAnalyzing(false);
    setScoreError('');
    setScoreResult(null);
    setScoreNewlyAdded(false);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      // Show "analyzing" after 3 seconds (means it's a new stock being processed)
      const analyzeTimer = setTimeout(() => {
        setScoreAnalyzing(true);
      }, 3000);

      const response = await fetch(`/api/public/score/${encodeURIComponent(ticker)}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      clearTimeout(analyzeTimer);
      setScoreAnalyzing(false);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          setScoreError(`"${ticker}" is not a recognized stock symbol. Double-check the ticker and try again.`);
        } else if (response.status === 400) {
          setScoreError(errData.error || 'Invalid ticker symbol.');
        } else {
          setScoreError(errData.error || 'Something went wrong. Please try again.');
        }
        return;
      }

      const data = await response.json();
      setScoreResult(data);
      setScoreSearch(ticker);
      
      if (data._source === 'newly_added') {
        setScoreNewlyAdded(true);
      }
    } catch (err) {
      setScoreAnalyzing(false);
      if (err.name === 'AbortError') {
        setScoreError('Request timed out — the analysis is taking longer than expected. Please try again in a minute.');
      } else {
        setScoreError('Unable to connect. Please try again.');
      }
    } finally {
      setScoreLoading(false);
      setScoreAnalyzing(false);
    }
  }, [scoreSearch]);

  const handleScoreKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleScoreLookup();
    }
  }, [handleScoreLookup]);

  // Helper to get score color/label
  const getScoreStyle = (score) => {
    if (score >= 85) return { color: 'text-purple-500', bg: 'bg-purple-500', ringColor: 'ring-purple-500/30', label: 'Very High', gradient: 'from-purple-500 to-purple-600' };
    if (score >= 70) return { color: 'text-green-500', bg: 'bg-green-500', ringColor: 'ring-green-500/30', label: 'High', gradient: 'from-green-500 to-emerald-600' };
    if (score >= 50) return { color: 'text-yellow-500', bg: 'bg-yellow-500', ringColor: 'ring-yellow-500/30', label: 'Moderate', gradient: 'from-yellow-500 to-amber-600' };
    if (score >= 30) return { color: 'text-orange-500', bg: 'bg-orange-500', ringColor: 'ring-orange-500/30', label: 'Low', gradient: 'from-orange-500 to-orange-600' };
    return { color: 'text-red-500', bg: 'bg-red-500', ringColor: 'ring-red-500/30', label: 'Very Low', gradient: 'from-red-500 to-red-600' };
  };

  // Google Analytics is loaded by <CookieConsent /> only after the visitor opts in.

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle escape key and body scroll lock for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowVideoModal(false);
      }
    };
    
    if (showVideoModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showVideoModal]);

  // Handle body scroll lock for mobile menu
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Positioning: the three questions every trade needs answered, mapped to
  // the product's three core reads (strength, timing, risk).
  const pillars = [
    {
      icon: Target,
      color: 'blue',
      question: 'Is it strong?',
      answer: 'TunaSignalAI Score',
      text: 'Every stock receives a score from 0–100, updated throughout the session. Sort the whole market by score in one click.'
    },
    {
      icon: Clock,
      color: 'green',
      question: 'Am I still early?',
      answer: 'Live Price % since flagged',
      text: 'See exactly how far a stock has moved since it was flagged — so you stop chasing moves that already happened.'
    },
    {
      icon: AlertTriangle,
      color: 'rose',
      question: 'What could go wrong?',
      answer: 'Risk tags',
      text: 'Dilution Risk, Illiquid and other badges surface red flags before you put money on the line.'
    }
  ];

  const pillarStyles = {
    blue:   darkMode ? 'bg-blue-900/40 text-sky-300' : 'bg-blue-100 text-blue-700',
    green:  darkMode ? 'bg-green-900/30 text-green-400'   : 'bg-green-100 text-green-600',
    rose:   darkMode ? 'bg-rose-900/30 text-rose-400'     : 'bg-rose-100 text-rose-600',
  };

  // Trial rewards ladder — rendered in the #trial-rewards section.
  const rewardStages = [
    {
      icon: Rocket,
      color: 'blue',
      when: `Market days 1–${TRIAL.marketDays}`,
      title: 'Start free',
      reward: 'Full platform access',
      text: 'Live Watch, Short Watch, Replay, ScalpWatch, every alert and Telegram for 5 market days. No credit card, nothing to cancel.'
    },
    {
      icon: Gift,
      color: 'green',
      when: 'When your trial ends',
      title: `${TRIAL.discountPct}% off`,
      reward: `Subscribe now or within ${TRIAL.redeemWindowHours} hours`,
      text: `Your trial-member discount is applied automatically at checkout — any time during your trial or up to ${TRIAL.redeemWindowHours} hours after it ends.`
    },
    {
      icon: Trophy,
      color: 'amber',
      when: `First ${TRIAL.discountMonths} months`,
      title: `$${DISCOUNTED_PRICE}/month`,
      reward: `Then $${TRIAL.standardPrice}/month`,
      text: 'Full platform access at half price for six months, then standard pricing. Cancel anytime.'
    }
  ];

  const rewardStyles = {
    blue:   { icon: darkMode ? 'bg-blue-900/30 text-blue-400'     : 'bg-blue-100 text-blue-600',     tag: darkMode ? 'text-blue-300'   : 'text-blue-700',   ring: 'border-blue-500' },
    green:  { icon: darkMode ? 'bg-green-900/30 text-green-400'   : 'bg-green-100 text-green-600',   tag: darkMode ? 'text-green-300'  : 'text-green-700',  ring: 'border-green-500' },
    amber:  { icon: darkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-600', tag: darkMode ? 'text-amber-300' : 'text-amber-700', ring: 'border-amber-500' },
  };

  const pricingPlans = [
    {
      name: 'TunaSignal AI — Trial',
      subtitle: `Full Platform Access • ${TRIAL.marketDays} US Market Days`,
      price: 'Free',
      period: `${TRIAL.marketDays} market days`,
      description: `Full access to every signal for ${TRIAL.marketDays} market days. ${TRIAL_OFFER_SENTENCE}`,
      features: [
        `${TRIAL.marketDays} market days of full access — weekends and holidays don't count`,
        `Subscribe during or ${TRIAL.redeemWindowHours}h after your trial → ${TRIAL.discountPct}% off your first ${TRIAL.discountMonths} months`,
        'AI signals ranked by TunaSignalAI Score',
        'All alerts: MACD, TunaSignalAI Score, SEC events, news, and volume spikes',
        'Unlimited stock monitoring',
        'Concise fundamental & technical summaries',
        'Advanced screening for high-potential setups',
        'Live Watch: scored list, ticker panel, live alert feed and one-click Telegram alerts per ticker',
        'Short Watch: fade stages, borrow & squeeze risk, costed short plans and a backtest',
        'Replay: play back past alerts bar by bar, or trade them blind in Practice mode',
        'ScalpWatch scalping dashboard with AI signals, voice alerts & Level 2 data',
        'Responsive web & mobile access',
        'Telegram & Email alerts with flexible rules',
        'Priority support'
      ],
      popular: false,
      buttonText: 'Start free trial',
      enabled: true
    },
    {
      name: 'TunaSignal AI — Monthly',
      subtitle: 'Complete platform for active traders',
      price: `$${TRIAL.standardPrice}`,
      period: 'month',
      offerNote: `$${DISCOUNTED_PRICE}/month for your first ${TRIAL.discountMonths} months when you subscribe during your trial or within ${TRIAL.redeemWindowHours} hours after it ends. Then $${TRIAL.standardPrice}/month. Cancel anytime.`,
      description: 'Full platform access with priority features and support. Cancel anytime.',
      features: [
        'AI signals ranked by TunaSignalAI Score',
        'All alerts: MACD, TunaSignalAI Score, SEC events, news, and volume spikes',
        'Unlimited stock monitoring',
        'Concise fundamental & technical summaries',
        'Advanced screening for high-potential setups',
        'Live Watch: scored list, ticker panel, live alert feed and one-click Telegram alerts per ticker',
        'Short Watch: fade stages, borrow & squeeze risk, costed short plans and a backtest',
        'Replay: play back past alerts bar by bar, or trade them blind in Practice mode',
        'ScalpWatch scalping dashboard with AI signals, voice alerts & Level 2 data',
        'Responsive web & mobile access',
        'Telegram & Email alerts with flexible rules',
        'Priority support'
      ],
      popular: false,
      buttonText: 'Subscribe',
      enabled: true
    }
  ];

  // FAQ — single list feeds both the visible FAQ and the JSON-LD schema so
  // the two can never drift apart.
  const faqItems = [
    {
      q: 'What is the TunaSignalAI Score?',
      a: 'The TunaSignalAI Score is a proprietary 0–100 analytical rating that combines MACD signals across multiple timeframes, relative volume, SEC filing activity and fundamental market indicators. Higher scores indicate stronger alignment across the evaluated factors, not a probability that the stock will rise and not a guaranteed trading outcome.'
    },
    {
      q: 'How do I know if I\'m early or late on a stock?',
      a: 'Every flagged stock shows a Live Price % — how far it has moved since TunaSignalAI flagged it. A high score with a small move since detection means you are still early; a large move means the setup may have already played out.'
    },
    {
      q: 'How does TunaSignalAI flag potential breakouts?',
      a: 'TunaSignalAI monitors thousands of stocks through the trading session. It checks MACD crossovers on timeframes from 1 minute to 1 month, flags unusual volume relative to historical baselines, and cross-references SEC filings and news sentiment, then alerts you when several signals line up. A flagged setup is a candidate to research, not a prediction that the stock will break out.'
    },
    {
      q: 'What is ScalpWatch?',
      a: 'ScalpWatch is TunaSignalAI\'s dedicated scalping dashboard. Prices stream live over WebSocket, and it adds seven AI signal types (STRONG BUY, BUY, SCALP, HOLD, TAKE PROFIT, SELL, WATCHING), a six-timeframe MACD cockpit, Level 2 order book data with wall detection, and a hands-free voice alert system. It is included with all plans.'
    },
    {
      q: 'What is Live Watch?',
      a: 'Live Watch is the main TunaSignalAI screen. It combines the scored stock list and the alert feed on one page: click any row and a ticker panel opens beside the list with 24-hour alert activity, the trade plan (stop, buy zone, targets), candles with VWAP, RSI and MACD, and dilution and short-squeeze risk. Prices update every 5 seconds and alerts every 20, and you can switch on Telegram alerts for a single ticker in one click.'
    },
    {
      q: 'What is Short Watch?',
      a: 'Short Watch checks the day\'s biggest gainers for a possible fade instead of a pump. Each stock moves through four stages (pump detected, exhaustion confirmed, executable, setup) and the row explains where it stopped and why. Borrow tier and fee, data freshness and squeeze risk must pass before anything is executable, and the short plan shows entry, stop, targets and reward-to-risk after costs. Short selling carries the risk of losses larger than your investment; always confirm borrow with your broker.'
    },
    {
      q: 'What is Replay?',
      a: 'Replay plays back any past TunaSignalAI alert bar by bar, with the score and plan frozen at alert time and no look-ahead. In Practice mode the ticker is hidden: you buy and sell when you would, and the outcome is revealed at the end. Results in Replay are simulated and do not predict future performance.'
    },
    {
      q: 'Is there a free trial? Do I need a credit card?',
      a: `Yes — TunaSignalAI offers a free trial of ${TRIAL.marketDays} US market days with no credit card required. Nothing is charged when it ends. You get unrestricted access to all platform features including the TunaSignalAI Score, Live Watch, Short Watch, Replay, ScalpWatch, real-time alerts, Telegram integration, and unlimited stock monitoring.`
    },
    {
      q: 'How much does TunaSignalAI cost?',
      a: `The Monthly plan is $${TRIAL.standardPrice}/month with full platform access, and you can cancel anytime. Trial members who subscribe during their trial or within ${TRIAL.redeemWindowHours} hours after pay $${DISCOUNTED_PRICE}/month for their first ${TRIAL.discountMonths} months.`
    },
    {
      q: 'How long is the free trial?',
      a: `Your trial covers your first ${TRIAL.marketDays} US market days. ${MARKET_DAY_RULE} If you sign up on a weekend or holiday, your trial starts on the next market day.`
    },
    {
      q: `How does the ${TRIAL.discountPct}% off offer work?`,
      a: `Subscribe to the Monthly plan during your trial or within ${TRIAL.redeemWindowHours} hours after it ends, and ${TRIAL.discountPct}% off is applied automatically to your first ${TRIAL.discountMonths} months ($${DISCOUNTED_PRICE}/month instead of $${TRIAL.standardPrice}/month). After that, standard pricing applies unless you cancel. Already on a trial from our earlier program? Your original terms still apply.`
    },
    {
      q: 'Does TunaSignalAI provide financial advice?',
      a: 'No. TunaSignalAI is a research and analytics platform that provides market data, AI-driven insights, and informational tools. Nothing on the platform constitutes investment, financial, legal, or tax advice. All content is for informational and research purposes only. Trading involves substantial risk — past signal performance is not indicative of future results.'
    }
  ];

  // Navigation handlers
  const handleGetStartedClick = () => {
    navigate('/register');
  };

  const handleSignInClick = () => {
    navigate('/login');
  };

  const handlePricingClick = (planName, planType) => {
    if (planType === 'trial') {
      navigate('/register');
    } else if (planType === 'monthly') {
      navigate('/subscription');
    }
  };

  const handleVideoPlay = () => {
    setShowVideoModal(true);
  };

  const handleVideoClose = () => {
    setShowVideoModal(false);
  };

  const handleLearnMoreClick = () => {
    navigate('/learn');
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:support@tunasignal.ai';
  };

  const handleLegalClick = (section) => {
    navigate(`/legal#${section}`);
  };

  const handleSocialClick = (platform) => {
    // Handle social clicks
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Video Modal Component
  const VideoModal = () => {
    if (!showVideoModal) return null;

    return (
      <div 
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        onClick={handleVideoClose}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        
        <div 
          className={`relative w-full max-w-4xl ${
            darkMode ? 'bg-[#07131F]' : 'bg-white'
          } rounded-xl shadow-2xl overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between p-4 border-b ${
            darkMode ? 'border-[#1C3348]' : 'border-slate-200'
          }`}>
            <h3 className={`text-lg font-semibold ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}>
              TunaSignal AI Platform Demo
            </h3>
            <button
              onClick={handleVideoClose}
              className={`p-2 rounded-md transition-colors ${
                darkMode 
                  ? 'hover:bg-[#0D1E2E] text-slate-400 hover:text-white' 
                  : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${youtubeVideoId}?autoplay=1&rel=0`}
              title="TunaSignal AI Platform Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          
          <div className={`p-4 border-t ${
            darkMode ? 'border-[#1C3348] bg-[#0D1E2E]/50' : 'border-slate-200 bg-slate-50'
          }`}>
            <div className="flex items-center justify-between">
              <p className={`text-sm ${
                darkMode ? 'text-slate-400' : 'text-slate-600'
              }`}>
                See how TunaSignal AI helps users identify and track potential market activity early.
              </p>
              <button
                onClick={() => {
                  handleVideoClose();
                  handleGetStartedClick();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };

  const metaDescription =
    `Find momentum before the crowd sees it. TunaSignalAI scores every stock from 0–100 and shows how far it has moved since it was flagged, so you know if you're still early. Try it free for ${TRIAL.marketDays} market days — no credit card.`;

  const navLinkClass = `transition ${darkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`;
  const mobileNavLinkClass = `px-4 py-3 rounded-md transition-all ${
    darkMode 
      ? 'text-slate-300 hover:text-white hover:bg-[#0D1E2E]' 
      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
  }`;

  return (
    <div className={`ts-page min-h-screen transition-colors ${darkMode ? 'bg-[#07131F]' : 'bg-white'}`}>
      {/* Post-logout prompt: one-click sign-in from the home page (mobile + desktop) */}
      {showLogoutToast && !mobileMenuOpen && (
        <div className="fixed bottom-4 inset-x-0 z-[60] flex justify-center px-4 pointer-events-none">
          <div
            className={`pointer-events-auto flex items-center gap-3 w-full max-w-md px-4 py-3 rounded-xl shadow-2xl border ${
              darkMode
                ? 'bg-[#0D1E2E] border-[#1C3348] text-slate-100'
                : 'bg-white border-slate-200 text-slate-800'
            }`}
          >
            <span className="flex-1 text-sm">You've been signed out.</span>
            <button
              onClick={() => { setShowLogoutToast(false); navigate('/login'); }}
              className="px-4 py-1.5 rounded-md text-sm bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5] transition"
            >
              Sign in
            </button>
            <button
              onClick={() => setShowLogoutToast(false)}
              aria-label="Dismiss"
              className={`p-1 rounded-md transition ${
                darkMode ? 'hover:bg-[#13283C] text-slate-400' : 'hover:bg-slate-100 text-slate-500'
              }`}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <Helmet>
        <title>AI Stock Scanner &amp; Trading Signals | TunaSignalAI</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="AI stock scanner, stock trading signals, momentum scanner, day trading scanner, AI trading tool, stock market AI, breakout stock scanner, unusual volume scanner, MACD scanner, short squeeze scanner, short selling scanner, stock replay, trading practice"
        />
        <meta property="og:title" content="AI Stock Scanner & Trading Signals | TunaSignalAI" />
        <meta
          property="og:description"
          content={`Find momentum stocks early — and know if you're still early. AI scores, live price since detection, risk tags and alerts. Try it free for ${TRIAL.marketDays} market days — no credit card.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tunasignal.ai" />
        <meta property="og:image" content="https://tunasignal.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Stock Scanner & Trading Signals | TunaSignalAI" />
        <meta
          name="twitter:description"
          content={`AI stock scanner: scores, early/late timing, risk tags and alerts. ${TRIAL_SHORT}.`}
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap"
        />
        <style>{`
          .ts-page { font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; color: #DCE6F0; }
          .ts-display { font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; font-weight: 600; letter-spacing: -0.025em; line-height: 1.04; }
          .ts-display-sub { font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; font-weight: 600; letter-spacing: -0.015em; line-height: 1.15; }
          .ts-num { font-family: 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace; font-variant-numeric: tabular-nums; }
          .ts-page a:focus-visible, .ts-page button:focus-visible, .ts-page input:focus-visible { outline: 2px solid #5AA9F0; outline-offset: 2px; }
        `}</style>
      </Helmet>

      {/* Video Modal */}
      <VideoModal />

      {/* How-it-works onboarding tour */}
      {showTour && (
        <LandingOnboardingOverlay
          darkMode={darkMode}
          onClose={() => setShowTour(false)}
          onGetStarted={() => { setShowTour(false); handleGetStartedClick(); }}
          audioBase="/audio/onboarding/"
        />
      )}

      {/* Announcement Bar — Webull Sync extension */}
      {showExtBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] h-10 bg-[#0D1E2E] text-[#DCE6F0] border-b border-[#1C3348]">
          <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <a
              href="#webull-sync"
              className="flex items-center min-w-0 flex-1 group"
            >
              <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 mr-2 rounded text-[10px] font-bold bg-[#D7263D] flex-shrink-0">
                NEW
              </span>
              <Chrome className="w-4 h-4 mr-2 flex-shrink-0 sm:hidden" />
              <span className="text-xs sm:text-sm font-medium truncate">
                <span className="sm:hidden">Sync your picks to Webull</span>
                <span className="hidden sm:inline">
                  Chrome extension: sync your TunaSignalAI picks straight to your Webull watchlist
                </span>
              </span>
              <span className="ml-2 text-xs sm:text-sm font-semibold underline underline-offset-2 whitespace-nowrap flex-shrink-0 group-hover:no-underline">
                Learn more
              </span>
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            </a>

            <div className="flex items-center flex-shrink-0 ml-3">
              <a
                href={WEBULL_EXTENSION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex items-center px-3 py-1 mr-2 rounded-md text-xs font-semibold bg-white text-blue-700 hover:bg-blue-50 transition"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Add to Chrome
              </a>
              <button
                onClick={dismissExtBanner}
                aria-label="Dismiss announcement"
                className="p-1 rounded hover:bg-white/20 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Header with Fixed Mobile Menu */}
      <header className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        showExtBanner ? 'top-10' : 'top-0'
      } ${
        scrolled 
          ? darkMode 
            ? 'bg-[#07131F]/95 backdrop-blur-md border-b border-[#1C3348]' 
            : 'bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm'
          : 'bg-transparent'
      }`}>
        <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src={tunaLogo} alt="TunaSignal AI" className="w-10 h-10" />
              <div className="flex flex-col"> 
                <span className={`text-xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  TunaSignal <span className={darkMode ? 'text-sky-400' : 'text-blue-700'}>AI</span>
                </span>
                <span className={`hidden sm:block text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  Find movers early. Know if you're still early.
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className={navLinkClass}>
                Features
              </a>
              <a href="#webull-sync" className={navLinkClass}>
                Webull Sync
              </a>
              <a href="#trial-rewards" className={navLinkClass}>
                Free Trial
              </a>
              <a href="#pricing" className={navLinkClass}>
                Pricing
              </a>
              <a href="/reports" className={navLinkClass}>
                Reports
              </a>
              <a href="/learn" className={navLinkClass}>
                Learn
              </a>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-3">
                <button
                  onClick={handleSignInClick}
                  className={`px-4 py-2 rounded-md transition ${
                    darkMode 
                      ? 'text-slate-300 hover:text-white hover:bg-[#0D1E2E]' 
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={handleGetStartedClick}
                  className="px-5 py-2.5 bg-[#5AA9F0] text-[#07131F] font-semibold rounded-md hover:bg-[#8CC4F5] transition-colors"
                >
                  <span className="flex items-center">
                    Start free trial
                  </span>
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={handleMobileMenuToggle}
                className={`md:hidden p-2 rounded-md transition-all ${
                  mobileMenuOpen 
                    ? darkMode 
                      ? 'bg-[#0D1E2E] text-white' 
                      : 'bg-slate-100 text-slate-900'
                    : darkMode 
                      ? 'hover:bg-[#0D1E2E] text-slate-400' 
                      : 'hover:bg-slate-100 text-slate-600'
                }`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu - Fixed Positioning */}
          {mobileMenuOpen && (
            <div className={`md:hidden absolute top-full left-0 right-0 z-50 ${
              darkMode 
                ? 'bg-[#07131F] border-b border-[#1C3348]' 
                : 'bg-white border-b border-slate-200'
            }`}>
              <div className="flex flex-col space-y-1 px-4 py-4">
                {/* Navigation Links */}
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>
                  Features
                </a>
                <a href="#webull-sync" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>
                  Webull Sync
                </a>
                <a href="#trial-rewards" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>
                  Free Trial
                </a>
                <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>
                  Pricing
                </a>
                <a href="/reports" onClick={() => setMobileMenuOpen(false)} className={mobileNavLinkClass}>
                  Reports
                </a>
                <button 
                  onClick={() => {
                    handleLearnMoreClick();
                    setMobileMenuOpen(false);
                  }}
                  className={`${mobileNavLinkClass} text-left`}
                >
                  Learn
                </button>
                
                {/* Divider */}
                <div className={`my-3 border-t ${darkMode ? 'border-[#1C3348]' : 'border-slate-200'}`} />
                
                {/* Action Buttons */}
                <button 
                  onClick={() => {
                    handleSignInClick();
                    setMobileMenuOpen(false);
                  }}
                  className={`px-6 py-3.5 text-center font-semibold rounded-md transition-all ${
                    darkMode 
                      ? 'bg-[#0D1E2E] text-white hover:bg-[#13283C] border border-[#1C3348]' 
                      : 'bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-300'
                  }`}
                >
                  Sign In
                </button>
                
                <button 
                  onClick={() => {
                    handleGetStartedClick();
                    setMobileMenuOpen(false);
                  }}
                  className="px-6 py-3.5 text-center font-semibold bg-[#5AA9F0] text-[#07131F] font-semibold rounded-md hover:bg-[#8CC4F5] transition-colors"
                >
                  <span className="flex items-center justify-center">
                    Start free trial
                  </span>
                </button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Overlay backdrop when menu is open */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Hero Section */}
      <section className={`relative pb-14 px-4 sm:px-6 lg:px-8 overflow-hidden transition-all duration-300 border-b ${
        darkMode ? 'bg-[#091826] border-[#1C3348]' : 'bg-[#EAF2F9] border-[#D3E3F1]'
      } ${showExtBanner ? 'pt-32' : 'pt-24'}`}>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center mb-12">
          <div className="text-left min-w-0">
            {/* Trial rewards pill */}
            <a
              href="#trial-rewards"
              className="inline-block mb-6 text-sm text-[#F2B33D] hover:text-[#F5C463] underline underline-offset-4 decoration-[#F2B33D]/40"
            >
              Free for {TRIAL.marketDays} market days, then {TRIAL.discountPct}% off your first {TRIAL.discountMonths} months
            </a>

            {/* Main Heading - compact */}
            <h1 className={`ts-display text-4xl sm:text-5xl mb-5 max-w-xl ${
              darkMode ? 'text-white' : 'text-[#0E2A47]'
            }`} style={{ textWrap: 'balance' }}>
              Find momentum before the crowd sees it
            </h1>

            {/* Subheading - shorter */}
            <p className={`text-base md:text-lg mb-7 max-w-xl ${
              darkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              TunaSignalAI Scores combine multi-timeframe momentum, volume, SEC filings and news sentiment into
              one number for every stock. Each signal also shows how far the stock has moved since it was flagged,
              so you know if you're still early or already late.
            </p>

            {/* CTA Buttons - compact inline */}
            <div className="flex flex-row gap-3 justify-start mb-3">
              <button
                onClick={handleGetStartedClick}
                className="group inline-flex items-center justify-center px-5 py-2.5 text-sm bg-[#5AA9F0] text-[#07131F] font-semibold rounded-md hover:bg-[#8CC4F5] transition-all duration-200"
              >
                Start free trial
              </button>
              <button
                onClick={() => setShowTour(true)}
                className={`inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-md border transition-colors ${
                  darkMode 
                    ? 'border-[#28445F] text-white hover:bg-[#13283C]' 
                    : 'border-[#0E2A47]/25 bg-white text-[#0E2A47] hover:border-[#0E2A47]/50'
                }`}
              >
                How it works
              </button>
            </div>
            <p className="text-xs text-slate-500">
              {TRIAL_SHORT}
            </p>

            {/* ---- Score Lookup Card - Theme-aware ---- */}
            <div id="score-lookup" className="max-w-xl mt-8 text-center">
              <div className={`relative rounded-md overflow-hidden border transition-colors ${
                darkMode
                  ? 'bg-[#0D1E2E]/80 border border-[#1C3348]/60'
                  : 'bg-white border-[#0E2A47]/15'
              }`}>
                {/* Top bar - uses the same blue gradient as the page CTA */}
                <div className={`${darkMode ? 'bg-[#07131F]' : 'bg-[#0E2A47]'} px-5 py-2.5 border-b border-[#1C3348] text-left`}>
                  <span className="text-sm font-semibold text-white">
                    Look up any stock's score
                  </span>
                  <span className="text-sm text-slate-400"> · free, no sign-up</span>
                </div>

                <div className="px-5 pt-4 pb-5">
                  {/* Search input */}
                  <div className={`flex items-center rounded-xl border-2 transition-all duration-200 ${
                    darkMode
                      ? 'bg-[#07131F]/60 border-[#28445F] focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20'
                      : 'bg-slate-50 border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20'
                  }`}>
                    <Search className={`ml-4 w-5 h-5 flex-shrink-0 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                    <input
                      ref={scoreInputRef}
                      type="text"
                      value={scoreSearch}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-Z.-]/g, '').slice(0, 7);
                        setScoreSearch(val);
                        setScoreError('');
                      }}
                      onKeyDown={handleScoreKeyDown}
                      placeholder="Enter ticker — AAPL, TSLA, NVDA..."
                      className={`flex-1 min-w-0 px-3 py-3.5 text-base font-semibold bg-transparent border-0 outline-none ${
                        darkMode ? 'text-white placeholder-slate-500' : 'text-slate-900 placeholder-slate-400'
                      }`}
                      autoComplete="off"
                      spellCheck="false"
                    />
                    <button
                      onClick={() => handleScoreLookup()}
                      disabled={scoreLoading || !scoreSearch.trim()}
                      className={`mr-1.5 px-5 py-2.5 rounded-md text-sm font-bold transition-all duration-200 flex items-center space-x-2 flex-shrink-0 ${
                        scoreLoading || !scoreSearch.trim()
                          ? darkMode
                            ? 'bg-[#13283C] text-slate-500 cursor-not-allowed'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5]'
                      }`}
                    >
                      {scoreLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                      <span>{scoreLoading ? (scoreAnalyzing ? 'Analyzing...' : 'Searching...') : 'Get Score'}</span>
                    </button>
                  </div>

                  {/* Analyzing progress message */}
                  {scoreAnalyzing && (
                    <div className="mt-3 flex items-center justify-center space-x-2 text-xs animate-pulse">
                      <Loader2 className={`w-3.5 h-3.5 animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                      <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                        New stock detected — running AI analysis. This may take up to 30 seconds...
                      </span>
                    </div>
                  )}

                  {/* Subtext */}
                  {!scoreAnalyzing && (
                    <p className={`text-center text-xs mt-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      AI confidence score for any stock — no sign-up required
                    </p>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {scoreError && (
                <div className={`mt-4 p-4 rounded-xl flex items-start space-x-3 ${
                  darkMode
                    ? 'bg-red-950/40 border border-red-800/40'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{scoreError}</p>
                </div>
              )}

              {/* Score Result Card */}
              {scoreResult && (() => {
                const score = scoreResult.confidence_score != null ? parseFloat(scoreResult.confidence_score) : null;
                const style = score != null ? getScoreStyle(score) : null;
                const price = scoreResult.close_price ? parseFloat(scoreResult.close_price) : null;
                const volume = scoreResult.volume ? parseInt(scoreResult.volume) : null;
                const marketCap = scoreResult.market_capitalization ? parseFloat(scoreResult.market_capitalization) : null;

                const formatMarketCap = (val) => {
                  if (!val) return 'N/A';
                  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
                  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
                  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
                  return `$${val.toLocaleString()}`;
                };

                const formatVolume = (val) => {
                  if (!val) return 'N/A';
                  if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
                  if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
                  return val.toLocaleString();
                };

                return (
                  <div className={`mt-6 rounded-xl overflow-hidden shadow-2xl border transition-all duration-500 animate-fade-in ${
                    darkMode
                      ? 'bg-[#0D1E2E] border-[#1C3348] shadow-black/40'
                      : 'bg-white border-slate-200 shadow-blue-100/50'
                  }`}>
                    {/* Score Header Bar */}
                    <div className={`${style?.bg || 'bg-slate-600'} p-5`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl font-black text-white">{scoreResult.ticker}</span>
                            {scoreResult.sector && (
                              <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                                {scoreResult.sector}
                              </span>
                            )}
                            {scoreNewlyAdded && (
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-white/30 text-white px-2 py-0.5 rounded-full animate-pulse">
                                Just added
                              </span>
                            )}
                          </div>
                          {scoreResult.industry && (
                            <p className="text-white/70 text-sm mt-1">{scoreResult.industry}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-4xl font-black text-white">
                            {score != null ? `${score.toFixed(0)}%` : 'N/A'}
                          </div>
                          <div className="text-white/80 text-xs font-medium mt-1">
                            TunaSignalAI Score
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Score Detail Grid */}
                    <div className="p-5">
                      {/* Score Bar */}
                      {score != null && (
                        <div className="mb-5">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Confidence Level
                            </span>
                            <span className={`text-sm font-bold ${style.color}`}>
                              {style.label}
                            </span>
                          </div>
                          <div className={`h-3 rounded-full overflow-hidden ${darkMode ? 'bg-[#13283C]' : 'bg-slate-100'}`}>
                            <div
                              className={`h-full rounded-full ${style.bg} transition-all duration-1000 ease-out`}
                              style={{ width: `${Math.min(score, 100)}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>0%</span>
                            <span className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>100%</span>
                          </div>
                        </div>
                      )}

                      {/* Key Metrics Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className={`p-3 rounded-xl ${darkMode ? 'bg-[#13283C]/50' : 'bg-slate-50'}`}>
                          <div className="flex items-center space-x-1.5 mb-1">
                            <DollarSign className={`w-3.5 h-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Price</span>
                          </div>
                          <span className={`text-base font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {price != null ? `$${price.toFixed(2)}` : 'N/A'}
                          </span>
                        </div>

                        <div className={`p-3 rounded-xl ${darkMode ? 'bg-[#13283C]/50' : 'bg-slate-50'}`}>
                          <div className="flex items-center space-x-1.5 mb-1">
                            <BarChart3 className={`w-3.5 h-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Volume</span>
                          </div>
                          <span className={`text-base font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {formatVolume(volume)}
                          </span>
                        </div>

                        <div className={`p-3 rounded-xl ${darkMode ? 'bg-[#13283C]/50' : 'bg-slate-50'}`}>
                          <div className="flex items-center space-x-1.5 mb-1">
                            <Target className={`w-3.5 h-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Market Cap</span>
                          </div>
                          <span className={`text-base font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {formatMarketCap(marketCap)}
                          </span>
                        </div>

                        <div className={`p-3 rounded-xl ${darkMode ? 'bg-[#13283C]/50' : 'bg-slate-50'}`}>
                          <div className="flex items-center space-x-1.5 mb-1">
                            <Activity className={`w-3.5 h-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Timeframe</span>
                          </div>
                          <span className={`text-base font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {scoreResult.target_timeframe || 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* MACD Status Row */}
                      {scoreResult.macd_status && (
                        <div className={`mt-3 p-3 rounded-xl flex items-center justify-between ${
                          darkMode ? 'bg-[#13283C]/30' : 'bg-slate-50'
                        }`}>
                          <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>MACD Status</span>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                            scoreResult.macd_status === 'green' || scoreResult.macd_status === 'bullish'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : scoreResult.macd_status === 'red' || scoreResult.macd_status === 'bearish'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-slate-100 text-slate-600 dark:bg-[#13283C] dark:text-slate-300'
                          }`}>
                            {scoreResult.macd_status.charAt(0).toUpperCase() + scoreResult.macd_status.slice(1)}
                          </span>
                        </div>
                      )}

                      {/* CTA Row */}
                      <div className={`mt-5 pt-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 ${
                        darkMode ? 'border-[#1C3348]' : 'border-slate-100'
                      }`}>
                        <div className="flex items-start space-x-2">
                          <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                          <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Sign up free to see Live Price % since detection, risk tags, alerts, and MACD timelines.
                          </p>
                        </div>
                        <button
                          onClick={handleGetStartedClick}
                          className="flex-shrink-0 px-5 py-2 bg-[#5AA9F0] text-[#07131F] font-semibold text-sm font-semibold rounded-md hover:bg-[#8CC4F5] transition-all flex items-center space-x-2"
                        >
                          <span>Start Free Trial</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

          </div>

          {/* Published results, one week behind the newest report */}
          <WeeklyResultsBoard />
          </div>

          <div className="text-center">

            {/* Trust line: plain text, no icon row */}
            <p className="mt-8 text-sm text-slate-400">
              Payments by Stripe · Live WebSocket prices · No card to start ·{' '}
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 decoration-slate-600 hover:text-white hover:decoration-slate-300"
              >
                Reviews on Trustpilot
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Positioning: Strength · Timing · Risk */}
      <section id="why" className="pt-8 pb-4 px-4 sm:px-6 lg:px-8 scroll-mt-28">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 max-w-3xl">
            <h2 className="ts-display text-3xl md:text-4xl mb-3 text-white">
              Three questions before every trade
            </h2>
            <p className="text-lg text-slate-400">
              Most scanners only tell you what's moving. TunaSignalAI answers all three on one screen.
            </p>
          </div>
          <div className="grid md:grid-cols-3 border-t border-[#28445F]">
            {pillars.map((p, i) => (
              <div
                key={`pillar-${i}`}
                className={`py-6 md:pr-8 ${i ? 'md:pl-8 md:border-l border-[#1C3348]' : ''} border-b md:border-b-0 border-[#1C3348]`}
              >
                <p className="ts-num text-xs text-slate-500 mb-3">0{i + 1}</p>
                <h3 className="text-xl font-semibold mb-1 text-white">
                  {p.question}
                </h3>
                <p className="text-sm font-medium mb-3 text-[#8CC4F5]">
                  {p.answer}
                </p>
                <p className="text-sm leading-relaxed text-slate-400">
                  {p.text}
                </p>
              </div>
            ))}
          </div>

          {/* Why not just use a screener? */}
          <div className="mt-14 rounded-md border border-[#1C3348] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#1C3348] bg-[#0D1E2E]">
              <h3 className="ts-display-sub text-xl text-white">Why not just use a stock screener?</h3>
              <p className="text-sm text-slate-400 mt-1">
                Screeners show you what is moving right now. TunaSignalAI helps you understand the strength, timing and risks behind each move.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-[#1C3348]">
                    <th className="font-medium px-4 sm:px-5 py-2.5"><span className="sr-only">Feature</span></th>
                    <th className="font-medium px-2 sm:px-4 py-2.5 text-center w-20 sm:w-40"><span className="sm:hidden">Screener</span><span className="hidden sm:inline">Typical screener</span></th>
                    <th className="font-medium px-2 sm:px-4 py-2.5 text-center w-20 sm:w-40 text-[#8CC4F5]">TunaSignal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { q: 'Lists stocks moving on price and volume', s: 'yes', t: 'yes' },
                    { q: 'One score from MACD across timeframes (1 minute to 1 month), volume, SEC filings and news', s: 'no', t: 'yes' },
                    { q: 'How far each stock has moved since it was flagged, so you know if you are late', s: 'no', t: 'yes' },
                    { q: 'Dilution and offering risk read from SEC filings', s: 'varies', t: 'yes' },
                    { q: 'Short-side fade checks with borrow, fee and squeeze risk', s: 'no', t: 'yes' },
                    { q: 'Replay any past signal bar by bar, or practise trading it blind', s: 'no', t: 'yes' },
                    { q: 'Every past signal published weekly, misses included', s: 'no', t: 'yes' },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-[#1C3348]/60 last:border-0">
                      <td className="px-4 sm:px-5 py-3 text-slate-300">{row.q}</td>
                      {[row.s, row.t].map((v, j) => (
                        <td key={j} className="px-2 sm:px-4 py-3 text-center">
                          {v === 'yes' ? (
                            <CheckCircle className={`inline w-4 h-4 ${j ? 'text-emerald-400' : 'text-slate-400'}`} aria-label="Yes" />
                          ) : v === 'no' ? (
                            <span className="text-slate-600" aria-label="No">No</span>
                          ) : (
                            <span className="text-slate-500 text-xs">Varies</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-[#1C3348] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <p className="text-xs text-slate-500">Don't take our word for it: every weekly report lists every signal and how it played out.</p>
              <a href="/reports" className="text-sm font-medium text-[#5AA9F0] hover:text-[#8CC4F5] whitespace-nowrap">See the weekly reports</a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section — four workspaces, one subscription */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 scroll-mt-28">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 max-w-3xl">
            <h2 className="ts-display text-3xl md:text-4xl mb-3 text-white">
              Four workspaces, one subscription
            </h2>
            <p className="text-lg text-slate-400">
              Live Watch for the long side, Short Watch for fading pumps, Replay to rehearse past alerts,
              and ScalpWatch for intraday scalping, all on the same score, the same alerts and one login.
            </p>
          </div>
          <WorkspacesShowcase onStart={handleGetStartedClick} />
        </div>
      </section>

      {/* Webull Watchlist Sync Extension Section */}
      <section id="webull-sync" className={`py-20 px-4 sm:px-6 lg:px-8 scroll-mt-28 ${
        darkMode ? 'bg-[#0D1E2E]/50' : 'bg-slate-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: copy + CTA */}
            <div>
              <p className="ts-num text-xs text-slate-500 mb-4">Free Chrome extension</p>

              <h2 className={`ts-display text-3xl md:text-4xl mb-4 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}>
                Send your picks straight to Webull
              </h2>

              <p className={`text-lg mb-8 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Stop copying tickers by hand. Set your criteria once — like every stock scoring 70 or
                higher — and the extension keeps your Webull watchlist matched to your TunaSignalAI
                scans automatically.
              </p>

              <div className="mb-8 border-t border-[#1C3348]">
                {[
                  {
                    icon: Target,
                    title: 'Your criteria, your watchlist',
                    text: 'Filter by TunaSignalAI Score, price, volume, or active buy signal.'
                  },
                  {
                    icon: RefreshCw,
                    title: 'Syncs in the background',
                    text: 'Runs every few minutes, or on demand whenever you want.'
                  },
                  {
                    icon: Lock,
                    title: 'Your login stays yours',
                    text: 'Works with your existing Webull browser session. No broker password needed.'
                  }
                ].map((item, i) => (
                  <div key={`ws-${i}`} className="py-3 border-b border-[#1C3348]">
                    <div>
                      <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {item.title}
                      </h4>
                      <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {item.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={WEBULL_EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5] transition-all"
                >
                  Add to Chrome, free
                </a>
                <a
                  href="/legal#webull-extension-privacy"
                  className={`inline-flex items-center justify-center px-6 py-3 rounded-md font-semibold transition ${
                    darkMode
                      ? 'bg-[#13283C] text-slate-200 hover:bg-slate-600'
                      : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-300'
                  }`}
                >
                  How it handles your data
                </a>
              </div>

              <p className={`text-xs mt-4 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                Requires a TunaSignalAI account and a Webull account. Not affiliated with Webull.
              </p>
            </div>

            {/* Right: how it works */}
            <div className="p-8 rounded-md bg-[#07131F] border border-[#1C3348]">
              <h3 className={`text-lg font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Up and running in three steps
              </h3>

              <div className="space-y-5">
                {[
                  {
                    n: '1',
                    title: 'Install the extension',
                    text: 'One click from the Chrome Web Store.'
                  },
                  {
                    n: '2',
                    title: 'Pick your criteria',
                    text: 'Open the gear icon on Stock Watch and choose which tickers should sync.'
                  },
                  {
                    n: '3',
                    title: 'Turn on auto-sync',
                    text: 'Your Webull watchlist updates itself from then on.'
                  }
                ].map((step, i) => (
                  <div key={`wstep-${i}`} className="flex items-start space-x-4">
                    <span className="ts-num text-sm text-slate-500 w-5 flex-shrink-0 pt-0.5">{step.n}</span>
                    <div>
                      <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {step.title}
                      </h4>
                      <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        {step.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mini sync-result preview */}
              <div className={`mt-8 pt-6 border-t ${darkMode ? 'border-[#1C3348]' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-emerald-400">
                      Synced
                    </span>
                  </div>
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    just now
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['GCTK', 'RKLZ', 'QBTZ', 'KPRX', 'PMAX', 'TSDD'].map((sym) => (
                    <span
                      key={`wsym-${sym}`}
                      className={`px-2 py-1 rounded text-xs font-mono font-semibold ${
                        darkMode ? 'bg-[#0D1E2E] text-slate-300' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {sym}
                    </span>
                  ))}
                  <span className={`px-2 py-1 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    +44 more
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trial Section */}
      <section id="trial-rewards" className="py-20 px-4 sm:px-6 lg:px-8 scroll-mt-28">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[5fr_7fr] gap-12">
          <div>
            <h2 className="ts-display text-3xl md:text-4xl mb-4 text-white">
              Try it free for 5 market days, then subscribe at half price
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              {TRIAL.marketDays} market days free, no card. Subscribe during your trial or within {TRIAL.redeemWindowHours} hours after
              and pay ${DISCOUNTED_PRICE}/month for your first {TRIAL.discountMonths} months, then ${TRIAL.standardPrice}/month.
            </p>
            <button
              onClick={handleGetStartedClick}
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold bg-[#5AA9F0] text-[#07131F] rounded-md hover:bg-[#8CC4F5] transition-colors"
            >
              Start day 1, free
            </button>
            <p className="text-xs text-slate-500 mt-3">Your remaining trial days are shown inside the app.</p>
          </div>

          <div>
            <table className="w-full text-sm border-t border-[#28445F]">
              <tbody>
                {rewardStages.map((st, i) => (
                  <tr key={`stage-${i}`} className="border-b border-[#1C3348] align-top">
                    <td className="py-5 pr-6 ts-num text-xs text-slate-500 whitespace-nowrap w-40">{st.when}</td>
                    <td className="py-5">
                      <p className="text-lg font-semibold text-white">{st.title} <span className="font-normal text-slate-400">· {st.reward}</span></p>
                      <p className="mt-1 text-slate-400 leading-relaxed">{st.text}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 className="mt-10 mb-3 text-base font-semibold text-white">How the trial works</h3>
            <ul className="space-y-2 text-sm text-slate-300 list-disc pl-5 marker:text-slate-600">
              {[
                `Your trial covers your first ${TRIAL.marketDays} US market days. Weekends and NYSE holidays don't use it up. Days roll over at midnight Eastern Time.`,
                'No card to start. When the trial ends, nothing is charged; your access simply pauses.',
                `Subscribe during your trial or within ${TRIAL.redeemWindowHours} hours after it ends and ${TRIAL.discountPct}% off is applied automatically to your first ${TRIAL.discountMonths} months.`,
                `After ${TRIAL.discountMonths} months the plan renews at $${TRIAL.standardPrice}/month. Cancel anytime.`,
                'Already on a trial from our earlier program? Your original terms still apply.'
              ].map((rule, i) => (
                <li key={`rule-${i}`}>{rule}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 scroll-mt-28 bg-[#0D1E2E]/50 border-t border-[#1C3348]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 max-w-3xl">
            <h2 className="ts-display text-3xl md:text-4xl mb-3 text-white">
              Pricing
            </h2>
            <p className="text-lg text-slate-400">
              One plan with everything in it. Start free for 5 market days, no card needed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className="rounded-md p-8 bg-[#07131F] border border-[#28445F] flex flex-col"
              >
                <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                {plan.subtitle && <p className="text-sm text-slate-400 mt-1">{plan.subtitle}</p>}
                <div className="flex items-baseline gap-2 mt-6">
                  <span className="ts-num text-4xl font-semibold text-white">{plan.price}</span>
                  {plan.price !== 'Free' && <span className="text-slate-400">/{plan.period}</span>}
                </div>
                {plan.offerNote && <p className="text-sm mt-2 text-[#F2B33D]">{plan.offerNote}</p>}
                <p className="text-sm text-slate-400 mt-3">{plan.description}</p>

                <ul className="mt-6 mb-8 border-t border-[#1C3348] text-sm text-slate-300">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="py-2.5 border-b border-[#1C3348] flex gap-3">
                      <CheckCircle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePricingClick(plan.name, index === 0 ? 'trial' : 'monthly')}
                  disabled={!plan.enabled}
                  className={`mt-auto w-full py-3 px-6 rounded-md font-semibold transition-colors ${
                    plan.enabled
                      ? index === 0
                        ? 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
                        : 'border border-[#5AA9F0] text-[#8CC4F5] hover:bg-[#13283C]'
                      : 'bg-[#13283C]/50 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  {plan.buttonText}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-slate-500 max-w-5xl">
            Your subscription renews at $200/month after the introductory period. Cancel anytime. Payments through Stripe; we never store your card.
            TunaSignal AI provides data and analysis for research only, not financial advice.
          </p>
        </div>
      </section>

      {/* SEO Content + FAQ Section */}
      <section className={`py-20 px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-[#0D1E2E]/50' : 'bg-slate-50'}`}>
        <div className="max-w-4xl mx-auto">

          {/* Indexable content block */}
          <div className="mb-16">
            <h2 className="ts-display text-3xl md:text-4xl mb-6 text-white">
              What is an AI stock scanner?
            </h2>
            <p className={`text-lg mb-6 leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              An AI stock scanner helps traders identify stocks showing unusual momentum, volume and technical activity. TunaSignalAI goes further by combining AI-powered scoring, multi-timeframe analysis, SEC filing insights and market activity to help traders evaluate potential opportunities.
            </p>
            <p className={`text-lg mb-6 leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Rather than simply showing which stocks are moving, TunaSignalAI helps you understand how strong a move may be, whether you're still early, and what risks could affect the trade. Every stock gets a <strong>TunaSignalAI Score</strong> from 0 to 100, an analytical rating of how strongly MACD, volume, SEC filings and fundamentals line up, plus <strong>how far it has moved since it was flagged</strong>.
            </p>

            <h2 className={`ts-display-sub text-2xl mb-4 mt-10 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Who TunaSignalAI is for
            </h2>
            <ul className={`space-y-2 text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              <li className="flex items-start"><span className="mr-2 text-blue-500 font-bold">•</span>Day traders looking for momentum stocks and intraday breakout setups</li>
              <li className="flex items-start"><span className="mr-2 text-blue-500 font-bold">•</span>Swing traders who want early detection of multi-day breakout patterns</li>
              <li className="flex items-start"><span className="mr-2 text-blue-500 font-bold">•</span>Scalpers who want live prices and signals in the ScalpWatch dashboard</li>
              <li className="flex items-start"><span className="mr-2 text-blue-500 font-bold">•</span>Experienced short sellers who want fade setups checked for borrow and squeeze risk in Short Watch</li>
              <li className="flex items-start"><span className="mr-2 text-blue-500 font-bold">•</span>Newer traders who want to rehearse past alerts bar by bar in Replay before risking real money</li>
              <li className="flex items-start"><span className="mr-2 text-blue-500 font-bold">•</span>Active retail investors who want AI-powered stock analysis without complexity</li>
            </ul>

            <h2 className={`ts-display-sub text-2xl mb-4 mt-10 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              How TunaSignalAI works
            </h2>
            <p className={`text-lg leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              TunaSignalAI monitors the market through the trading session using a live WebSocket price feed. Prices in Live Watch update every 5 seconds and alerts are checked every 20 seconds. When a stock shows several signals at once, such as a MACD crossover on multiple timeframes together with volume well above its usual level, the system raises an alert and updates the TunaSignalAI Score. Alerts can go to Telegram and email so you don't have to watch the screen.
            </p>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="ts-display text-3xl md:text-4xl mb-8 text-white">
              Questions people ask
            </h2>
            <div className="border-t border-[#28445F]">
              {faqItems.map((item, i) => (
                <div key={i} className="py-6 border-b border-[#1C3348]">
                  <h3 className="text-lg font-semibold mb-2 text-white">
                    {item.q}
                  </h3>
                  <p className={`leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0D1E2E] border-y border-[#1C3348]">
        <div className="max-w-4xl mx-auto">
          <h2 className="ts-display text-3xl md:text-4xl text-white mb-4">
            Stop finding movers after they've moved
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl">
            Live Watch, Short Watch, Replay and ScalpWatch, free for {TRIAL.marketDays} market days.
            Subscribe within {TRIAL.redeemWindowHours} hours of the trial ending and your first {TRIAL.discountMonths} months are {TRIAL.discountPct}% off.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleGetStartedClick}
              className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold bg-[#5AA9F0] text-[#07131F] rounded-md hover:bg-[#8CC4F5] transition-colors"
            >
              Start your free trial
            </button>
            <button
              onClick={() => setShowTour(true)}
              className="inline-flex items-center justify-center px-7 py-3.5 text-base font-medium text-white border border-[#28445F] rounded-md hover:bg-[#13283C] transition-colors"
            >
              See how it works
            </button>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-10 pt-6 border-t border-[#1C3348]">
            <p className="text-sm text-slate-400">
              <strong>Risk disclaimer:</strong> TunaSignal AI (including Live Watch, Short Watch, Replay, ScalpWatch, Stock Watch and Alerts) is a research and analytics platform that provides market data, 
              AI-driven insights, and informational tools. We do <strong>not</strong> provide investment, financial, legal, 
              or tax advice. Nothing on this platform should be interpreted as a recommendation to buy, sell, or hold 
              any security. All content is for <strong>informational and research purposes only</strong>.
            </p>
            <p className="text-sm text-slate-400 mt-2">
              Trading and investing involve substantial risk, including the possible loss of principal. Day trading and scalping 
              carry especially high risk — the majority of day traders lose money. Short selling can lose more than the amount invested, 
              and borrow, fees and squeezes can change quickly. Replay, Practice and backtest results are simulated. Past performance 
              of any signal, score, or data output is <strong>not indicative of future results</strong>. Users are solely 
              responsible for their own investment decisions and should consult a qualified financial advisor before 
              making financial choices.
            </p>
            <p className="text-sm text-slate-400 mt-2">
              By using TunaSignal AI, you acknowledge and agree that the platform and its creators are not responsible 
              for any losses or damages arising from reliance on the information provided.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 px-4 sm:px-6 lg:px-8 border-t ${
        darkMode ? 'bg-[#07131F] border-[#1C3348]' : 'bg-white border-slate-200'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <img src={tunaLogo} alt="TunaSignal AI" className="w-8 h-8" />
                <span className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  TunaSignal AI
                </span>
              </div>
              <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Find movers early. Know if you're still early.
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                <strong>Important:</strong> We are not financial advisors. All investments involve risk.
              </p>
              {/* Follow us */}
              <div className="mt-5">
                <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>Follow us</p>
                <div className="flex space-x-4">
                  <a href="https://x.com/TunaSignalAI" target="_blank" rel="noopener noreferrer" aria-label="TunaSignalAI on X" title="X (Twitter)"
                    className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  </a>
                  <a href="https://www.instagram.com/tunasignal.ai/" target="_blank" rel="noopener noreferrer" aria-label="TunaSignalAI on Instagram" title="Instagram"
                    className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
                  </a>
                  <a href="https://www.tiktok.com/@tunasignalai" target="_blank" rel="noopener noreferrer" aria-label="TunaSignalAI on TikTok" title="TikTok"
                    className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" /></svg>
                  </a>
                  <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer" aria-label="TunaSignalAI reviews on Trustpilot" title="Trustpilot reviews"
                    className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                    <Star className="w-5 h-5" />
                  </a>
                  <a href="https://www.youtube.com/@TunaSignalAI" target="_blank" rel="noopener noreferrer" aria-label="TunaSignalAI on YouTube" title="YouTube"
                    className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Product
              </h4>
              <ul className="space-y-2">
                <li>
                  <a href="#features" className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href={WEBULL_EXTENSION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm inline-flex items-center ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Webull Sync Extension
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <a href="#trial-rewards" className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Trial Rewards
                  </a>
                </li>
                <li>
                  <a href="#pricing" className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="/reports" className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Performance Reports
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/learn#livewatch')}
                    className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Live Watch Guide
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/learn#shortwatch')}
                    className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Short Watch Guide
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/learn#replay')}
                    className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Replay Guide
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowTour(true)}
                    className={`text-sm inline-flex items-center ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    How it works
                    <PlayCircle className="w-3 h-3 ml-1" />
                  </button>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Support
              </h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={handleContactClick} className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Contact Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={handleEmailClick}
                    className={`text-sm inline-flex items-center ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    <Mail className="w-3 h-3 mr-1" />
                    support@tunasignal.ai
                  </button>
                </li>
                <li>
                  <a
                    href={TRUSTPILOT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm inline-flex items-center ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Reviews on Trustpilot
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    Cancel anytime from your profile
                  </span>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Legal
              </h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => handleLegalClick('privacy')} className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button onClick={() => handleLegalClick('terms')} className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Terms &amp; Conditions
                  </button>
                </li>
                <li>
                  <button onClick={() => handleLegalClick('refund')} className={`text-sm ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    Refund Policy
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className={`pt-8 border-t ${darkMode ? 'border-[#1C3348]' : 'border-slate-200'}`}>
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  © {new Date().getFullYear()} TunaSignal AI, Wyoming LLC. All rights reserved.
                </p>
                <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  TunaSignal AI, LLC, 30 N Gould St Ste N, Sheridan, WY 82801
                </p>
              </div>
              
              {/* Social Links */}
              <div className="flex space-x-5 mt-4 md:mt-0">
                <a
                  href="https://x.com/TunaSignalAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TunaSignalAI on X"
                  title="X (Twitter)"
                  className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/tunasignal.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TunaSignalAI on Instagram"
                  title="Instagram"
                  className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@tunasignalai"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TunaSignalAI on TikTok"
                  title="TikTok"
                  className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/@TunaSignalAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TunaSignalAI on YouTube"
                  title="YouTube"
                  className={`${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>

              {/* Security Badges */}
              <p className="text-xs text-slate-500 mt-4 md:mt-0">Payments handled by Stripe</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
